/**
 * SamlelistePage
 * 
 * Tablet-optimized bulk equipment creation with smart cascading dropdowns.
 * Minimizes typing - uses dropdowns based on category → manufacturer → product type.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Copy, Download, CheckCircle, AlertCircle, Loader2, Search, X, Pencil, Check, Camera } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { BulkEquipmentService } from '../services/BulkEquipmentService'
import type { EquipmentEntry } from '../types'
import { generateTempId } from '../types'
import { usePageHeader } from '@/components/layout/pageHeader'
import { matchesAnyCategoryFamily, matchesCategoryFamily } from '@/utils/categoryFamily'

// Compact touch-friendly dimensions for tablet
const TOUCH = {
  minTarget: 44,
  buttonHeight: 'min-h-[44px]',
  inputHeight: 'min-h-[44px]',
  fontSize: 'text-base',
}

// Types for smart dropdowns
interface SamlelisteCategory {
  id: string
  code: string
  name_no: string
  name_en: string
  subcategory_id: string | null
  quantity_options: number[] | null
  has_batch_bolt: boolean
  has_batch_bow: boolean
}

interface SamlelisteManufacturer {
  id: string
  name: string
  short_name: string | null
}

interface SamlelisteProductType {
  id: string
  type_name: string
  type_code: string | null
  description: string | null
  default_specs: Record<string, string> | null
  wll_options: string[] | null
  dimension_options: Record<string, string> | null  // WLL → dimension mapping
  standard: string | null
  grade: string | null
}

// Spec template for custom categories
interface CustomSpecTemplate {
  id: string
  spec_key: string
  label_no: string | null
  label_en: string | null
  data_type: string
  is_required: boolean
  display_order: number
  unit_symbol?: string
  default_value?: string
}


// Inspection status options - always show colors, selected has ring
const INSPECTION_STATUSES = [
  { value: 'OK', label: 'OK', color: 'bg-emerald-500', selectedRing: 'ring-emerald-700' },
  { value: 'C', label: 'C - Betinget', color: 'bg-yellow-500', selectedRing: 'ring-yellow-700' },
  { value: 'MO', label: 'MO - Må Overhaules', color: 'bg-amber-500', selectedRing: 'ring-amber-700' },
  { value: 'RC', label: 'RC - Rekondisjoneres', color: 'bg-orange-500', selectedRing: 'ring-orange-700' },
  { value: 'NC', label: 'NC - Ikke Godkjent', color: 'bg-red-500', selectedRing: 'ring-red-700' },
] as const

// Categories allowed in Utstyrstype dropdown (limited set for now)
const SEARCHABLE_CATEGORY_CODES = [
  'R1',     // Kjetting (Chain Sling)
  'R3',     // Ståltau (Wire Sling)
  'R4-2',   // Sjakkel (all shackle types - user selects subtype)
  'R4-4',   // Øyebolt (Eye Bolt)
  'R2-1',   // Rundsling (Fiber Roundsling)
  'R2-2',   // Flatvevd Båndsling (Flatwoven Webbing Sling)
]

// Shackle subtypes (shown after selecting "Sjakkel")
type ShackleType = 'offshore' | 'd-sjakkel' | 'h-sjakkel'
const SHACKLE_TYPES: Array<{ value: ShackleType; label: string; categoryCode: string }> = [
  { value: 'offshore', label: 'Offshore Sjakkel', categoryCode: 'R4-2' },
  { value: 'd-sjakkel', label: 'D-Sjakkel', categoryCode: 'R4-3' },
  { value: 'h-sjakkel', label: 'H-Sjakkel', categoryCode: 'R4-2' }, // H-Sjakkel uses same logic as offshore
]

// Categories to exclude from dropdown (not used currently)
const EXCLUDED_CATEGORY_NAMES: string[] = []

// Parent categories to include all subcategories from (none - using specific codes)
const SEARCHABLE_PARENT_CATEGORIES: string[] = []

// Display name overrides for categories (code → Norwegian display name)
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  // Sling categories
  'R1': 'Kjetting',
  'R3': 'Ståltau',
  'R2-1': 'Rundsling',
  'R2-2': 'Flatvevd Båndsling',
  // Specific categories with Norwegian names
  'R4-2': 'Sjakkel',  // Main shackle category - user selects subtype (Offshore/D/H)
  'R4-3': 'D-Sjakkel', // Keep for backwards compatibility and custom selections
  'R4-4': 'Øyebolt',
  'R4-11': 'Bjelkeklemme',
  'R4-12': 'Klemme',
  'R4-15': 'Lastcelle',
  'R9-6': 'Masterlink',
  'R9-21': 'Krok',
  'FAL': 'Fallsikring',
  'FAL-1': 'Fallsele',
  'FAL-2': 'Selvlåsende glider (fleksibel)',
  'FAL-3': 'Selvlåsende glider (stiv)',
  'FAL-4': 'Forbindelsesline',
  'FAL-5': 'Falldemper',
  'FAL-6': 'Belter og støttestropper',
  'FAL-7': 'Fallblokk',
  'FAL-8': 'Karabinkrok',
  'FAL-9': 'Forankringsanordning',
  'FAL-10': 'Stige',
}

// Note: R4-2 (Shackle) uses samleliste_category_manufacturers table
// All other categories use manufacturers_categories table from database

// User-friendly spec labels (Norwegian/bilingual like equipment component)
const SPEC_DISPLAY_LABELS: Record<string, string> = {
  'wll': 'WLL',
  'swl': 'SWL',
  'manufacturer': 'Produsent/Manufacturer',
  'prod_year': 'Prod.år/Prod. Year',
  'prod_acc_to': 'Prod. iht./Prod. Acc. To',
  'dim_mm': 'Dim (mm)',
  'diameter': 'Diameter',
  'length': 'Lengde/Length',
  'leg_length_m': 'Legglengde (m)/Leg Length (m)',
  'grade': 'Grade',
  'certificate': 'Sertifikat/Certificate',
  'batch_id_bow': 'Batch ID Bue/Bow',
  'batch_id_bolt': 'Batch ID Bolt',
  'serial_number': 'Serienr./Serial No.',
  'part_no': 'Part No.',
  'owner_id': 'Eiers ID/Owner ID',
  'last_ndt_date': 'Siste NDT/Last NDT',
  'ndt_date': 'Siste NDT/Last NDT',
  'next_ndt_date': 'Neste NDT/Next NDT',
  'iwrc': 'IWRC',
  'no_of_legs': 'Antall løft/No. of Legs',
  'no_of_shackles': 'Antall sjakler/No. of Shackles',
  'tare': 'Tara/Tare',
  'test_load': 'Prøvelast/Test Load',
  'working_temp': 'Arb.temp/Working Temp',
  'material': 'Materiale/Material',
  'color': 'Farge/Color',
  'capacity': 'Kapasitet/Capacity',
  'quantity': 'Antall/Quantity',
  'thread_type': 'Gjenge/Thread',
}

// Specification display item (label + value + editable info)
interface SpecDisplayItem {
  spec_key: string    // For editing
  label: string
  value: string
  priority: number
  unit_symbol?: string  // For display after editing
}

// Existing equipment search result type
interface ExistingEquipment {
  id: string
  name: string
  serial_number: string
  asset_number: string
  category_id: string
  category_code: string
  category_name: string
  manufacturer_id: string | null
  manufacturer_name: string | null
  // All specs from equipment_specifications_v2 with resolved labels
  allSpecs: SpecDisplayItem[]
  // Key specs for quick access (also in allSpecs)
  wll: string | null
  dimension: string | null
  batch_id_bow: string | null
  batch_id_bolt: string | null
  standard: string | null
  grade: string | null
  quantity: number | null  // Antall - for display in search results
}

// Extended entry with WLL, batch IDs, and existing equipment tracking
interface SmartEquipmentEntry extends EquipmentEntry {
  product_type_id?: string
  wll?: string
  dimension?: string  // Auto-filled based on WLL
  capacity?: number   // Kapasitet (kg) for FAL categories
  standard?: string
  grade?: string
  quantity?: number
  batch_id_bow?: string
  batch_id_bolt?: string
  inspectionStatus?: string
  remarks?: string
  // Photo for non-OK statuses (stored as base64 data URL)
  inspectionPhoto?: string
  // NDT date for R1/R3 categories (dd.mm.yyyy format)
  ndtDate?: string
  // Generate NDT report (MT/MPI) for chain slings - only shown if user has MT qualification
  generateNdtReport?: boolean
  // Number of parts/legs for slings (1-5) - determines subcategory R1-1 thru R1-5 or R3-1 thru R3-5
  slingParts?: number
  // If set, this is an existing equipment (update control card, don't create new)
  existing_equipment_id?: string
  // Display names for existing equipment (stored from search, not from dropdowns)
  _existing_category_name?: string
  _existing_manufacturer_name?: string
  // All specs for existing equipment (displayed like control card)
  _existing_all_specs?: SpecDisplayItem[]
  // Original quantity from existing equipment (for inventory split detection)
  _original_quantity?: number
  // Original category/manufacturer for inventory split (creating new equipment)
  _original_category_id?: string
  _original_manufacturer_id?: string
  // Custom category selection ("Tilpasset" option)
  isCustomCategory?: boolean
  // Manual inputs for custom categories (text instead of IDs)
  customManufacturerName?: string
  // Shackle type selection (for R4-2 category)
  shackleType?: ShackleType
  // Custom spec values for Tilpasset categories (spec_key -> value)
  customSpecValues?: Record<string, string>
}

// Empty entry template
const createEmptyEntry = (): SmartEquipmentEntry => ({
  id: generateTempId(),
  name: '',
  serial_number: '',
  category_id: '',
  manufacturer_id: '',
  product_type_id: '',
  wll: '',
  standard: '',
  grade: '',
  quantity: undefined,
  batch_id_bow: '',
  batch_id_bolt: '',
  model: '',
  location: '',
  manufacture_year: undefined,
  inspectionStatus: 'OK',
  remarks: '',
  status: 'pending',
  errors: [],
  isCustomCategory: false,
  customManufacturerName: '',
  shackleType: undefined,
  customSpecValues: {}
})

export function SamlelistePage() {
  const navigate = useNavigate()
  const { companyId } = useParams<{ companyId: string }>()
  const scrollRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({})
  
  // Flow data from SamlelisteFlow
  const [orderNumber, setOrderNumber] = useState('')
  const [serviceOrder, setServiceOrder] = useState('') // TechnipFMC Service Order (so_no)
  const [flowLocation, setFlowLocation] = useState('')
  
  // Data loading
  const [loading, setLoading] = useState(true)
  const [companyName, setCompanyName] = useState('')
  const [currentUser, setCurrentUser] = useState<{ id: string; name: string; isAdmin: boolean; signature?: string } | null>(null)
  const [userHasMtQualification, setUserHasMtQualification] = useState(false)
  
  // Role-based selection
  // Admins select an inspector (who performed), Inspectors select an admin (who is responsible)
  const [inspectors, setInspectors] = useState<Array<{ id: string; name: string; signature?: string; hasMtQualification?: boolean }>>([])
  const [supervisors, setSupervisors] = useState<Array<{ id: string; name: string; signature?: string }>>([])
  const [selectedInspectorId, setSelectedInspectorId] = useState<string>('') // For admins to select performer
  
  // Smart dropdown data
  const [categories, setCategories] = useState<SamlelisteCategory[]>([])
  const [allAvailableCategories, setAllAvailableCategories] = useState<SamlelisteCategory[]>([]) // All categories for "Tilpasset" option
  const [allManufacturers, setAllManufacturers] = useState<Map<string, SamlelisteManufacturer[]>>(new Map())
  const [allProductTypes, setAllProductTypes] = useState<Map<string, SamlelisteProductType[]>>(new Map())
  
  // Samleliste category ID mapping (for shackles - maps categories_v2 ID to samleliste_categories ID)
  const [samlelisteCategoryMap, setSamlelisteCategoryMap] = useState<Map<string, string>>(new Map())
  
  // Entry state
  const [entries, setEntries] = useState<SmartEquipmentEntry[]>([createEmptyEntry()])
  
  // Equipment search state
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState<Record<string, string>>({})
  const [equipmentSearchResults, setEquipmentSearchResults] = useState<Record<string, ExistingEquipment[]>>({})
  const [isSearching, setIsSearching] = useState<Record<string, boolean>>({})
  const [searchableCategories, setSearchableCategories] = useState<string[]>([])
  
  // Custom category search state (for Tilpasset)
  const [customCategorySearch, setCustomCategorySearch] = useState<Record<string, string>>({})
  const [showCategoryDropdown, setShowCategoryDropdown] = useState<Record<string, boolean>>({})
  
  // Spec editing state: { entryId: { specKey: boolean } }
  const [editingSpecs, setEditingSpecs] = useState<Record<string, Record<string, boolean>>>({})
  
  // Custom spec templates per category: { categoryId: CustomSpecTemplate[] }
  const [customSpecTemplates, setCustomSpecTemplates] = useState<Map<string, CustomSpecTemplate[]>>(new Map())
  
  // Defaults (applied to all new entries - location comes from flow)
  const [defaultLocation, setDefaultLocation] = useState('')
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' })
  const [result, setResult] = useState<{ 
    success: boolean; 
    created: number; 
    failed: number;
    blob?: Blob 
  } | null>(null)

  // Load flow data from sessionStorage
  useEffect(() => {
    const storedOrderNumber = sessionStorage.getItem('samlelisteOrderNumber')
    const storedServiceOrder = sessionStorage.getItem('samlelisteServiceOrder')
    const storedLocation = sessionStorage.getItem('samlelisteLocation')
    
    if (storedOrderNumber) {
      setOrderNumber(storedOrderNumber)
      sessionStorage.removeItem('samlelisteOrderNumber')
    }
    
    if (storedServiceOrder) {
      setServiceOrder(storedServiceOrder)
      sessionStorage.removeItem('samlelisteServiceOrder')
    }
    
    if (storedLocation) {
      setFlowLocation(storedLocation)
      setDefaultLocation(storedLocation)
      sessionStorage.removeItem('samlelisteLocation')
    }
  }, [])

  // Load initial data
  useEffect(() => {
    loadInitialData()
    loadSearchableCategories()
  }, [companyId])

  // Load searchable category IDs from categories_v2
  const loadSearchableCategories = async () => {
    try {
      // Get all categories
      const { data: allCats } = await supabase
        .from('categories_v2')
        .select('id, code, parent_id')
      
      if (!allCats) return
      
      const categoryIds: string[] = []
      
      // Add specific category codes
      for (const cat of allCats) {
        if (SEARCHABLE_CATEGORY_CODES.includes(cat.code)) {
          categoryIds.push(cat.id)
        }
      }
      
      // Add all subcategories of parent categories (R2, G10, FAL)
      const parentCats = allCats.filter(c => SEARCHABLE_PARENT_CATEGORIES.includes(c.code))
      for (const parent of parentCats) {
        // Add parent itself
        categoryIds.push(parent.id)
        // Add all children
        const children = allCats.filter(c => c.parent_id === parent.id)
        for (const child of children) {
          categoryIds.push(child.id)
        }
      }
      
      const uniqueCategories = [...new Set(categoryIds)]
      console.log('🔍 Loaded searchable categories:', uniqueCategories.length, uniqueCategories)
      setSearchableCategories(uniqueCategories)
    } catch (err) {
      console.error('Failed to load searchable categories:', err)
    }
  }

  const loadInitialData = async () => {
    if (!companyId) return
    
    try {
      setLoading(true)
      
      // Load categories from categories_v2 - filter by specific codes and parent categories
      const { data: allCatsData } = await supabase
        .from('categories_v2')
        .select('id, code, title_no, title_en, parent_id')
        .order('code')
      
      if (allCatsData) {
        // Filter to only show specified categories
        const filteredCategories: SamlelisteCategory[] = []
        
        // Find parent category IDs for R1, R2, R3, G10
        const parentCats = allCatsData.filter(c => SEARCHABLE_PARENT_CATEGORIES.includes(c.code))
        const parentIds = parentCats.map(p => p.id)
        
        for (const cat of allCatsData) {
          // Include specific category codes
          const isSpecificCategory = SEARCHABLE_CATEGORY_CODES.includes(cat.code)
          // Include subcategories of R1, R2, R3, G10
          const isSubcategoryOfParent = cat.parent_id && parentIds.includes(cat.parent_id)
          // Include the parent categories themselves
          const isParentCategory = SEARCHABLE_PARENT_CATEGORIES.includes(cat.code)
          
          // Check if this category should be excluded by name
          const categoryName = cat.title_en || cat.title_no || ''
          const isExcluded = EXCLUDED_CATEGORY_NAMES.some(
            excluded => categoryName.toLowerCase() === excluded.toLowerCase()
          )
          
          if ((isSpecificCategory || isSubcategoryOfParent || isParentCategory) && !isExcluded) {
            // Determine if this category needs batch ID fields (offshore shackles)
            const needsBatch = cat.code === 'R4-2'
            
            filteredCategories.push({
              id: cat.id,
              code: cat.code,
              name_no: cat.title_no || cat.title_en || cat.code,
              name_en: cat.title_en || cat.title_no || cat.code,
              subcategory_id: cat.parent_id,
              quantity_options: null, // Default - no quantity options
              has_batch_bolt: needsBatch,
              has_batch_bow: needsBatch
            })
          }
        }
        
        // Sort: specific categories first (by display name), then subcategories alphabetically
        filteredCategories.sort((a, b) => {
          const aDisplay = CATEGORY_DISPLAY_NAMES[a.code] || a.name_en
          const bDisplay = CATEGORY_DISPLAY_NAMES[b.code] || b.name_en
          return aDisplay.localeCompare(bDisplay)
        })
        
        setCategories(filteredCategories)
        
        // Store ALL categories for "Tilpasset" (custom) option
        const allCategoriesForCustom: SamlelisteCategory[] = allCatsData.map(cat => ({
          id: cat.id,
          code: cat.code,
          name_no: cat.title_no || cat.title_en || cat.code,
          name_en: cat.title_en || cat.title_no || cat.code,
          subcategory_id: cat.parent_id,
          quantity_options: null,
          has_batch_bolt: cat.code === 'R4-2',
          has_batch_bow: cat.code === 'R4-2'
        }))
        
        // Sort by display name (use Norwegian if available, else code)
        allCategoriesForCustom.sort((a, b) => {
          const aDisplay = CATEGORY_DISPLAY_NAMES[a.code] || a.name_no || a.code
          const bDisplay = CATEGORY_DISPLAY_NAMES[b.code] || b.name_no || b.code
          return aDisplay.localeCompare(bDisplay)
        })
        
        setAllAvailableCategories(allCategoriesForCustom)
        console.log('📦 Loaded allAvailableCategories:', allCategoriesForCustom.length, 'categories')
        
        // Pre-load manufacturers for each category
        const mfrMap = new Map<string, SamlelisteManufacturer[]>()
        const samlelisteCatMap = new Map<string, string>()
        
        // Load all manufacturers from manufacturers_v2
        const { data: allMfrs } = await supabase
          .from('manufacturers_v2')
          .select('id, name, short_name')
          .order('name')
        
        // For R4-2 (Shackle), load from samleliste tables
        // Note: samleliste_categories uses 'R4' for shackles, categories_v2 uses 'R4-2'
        const shackleCat = filteredCategories.find(c => c.code === 'R4-2')
        console.log('Shackle category found:', shackleCat)
        
        if (shackleCat) {
          // First, get the samleliste_categories ID for R4 (Shackles in samleliste)
          const { data: samlelisteCatData, error: samlelisteCatError } = await supabase
            .from('samleliste_categories')
            .select('id, code')
            .eq('code', 'R4')
            .single()
          
          console.log('Samleliste category for R4:', { samlelisteCatData, samlelisteCatError })
          
          if (samlelisteCatData) {
            // Store the mapping from categories_v2 ID to samleliste_categories ID
            samlelisteCatMap.set(shackleCat.id, samlelisteCatData.id)
            console.log('Category mapping set:', shackleCat.id, '->', samlelisteCatData.id)
            
            // Load manufacturers using the samleliste category ID
            const { data: shackleMfrs } = await supabase
              .from('samleliste_category_manufacturers')
              .select(`
                manufacturer:samleliste_manufacturers(id, name, short_name)
              `)
              .eq('category_id', samlelisteCatData.id)
              .order('is_primary', { ascending: false })
            
            if (shackleMfrs && shackleMfrs.length > 0) {
              mfrMap.set(shackleCat.id, shackleMfrs.map((m: any) => m.manufacturer).filter(Boolean))
            } else if (allMfrs) {
              // Fallback to all manufacturers if no samleliste config
              mfrMap.set(shackleCat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
            }
          } else if (allMfrs) {
            // No samleliste entry found - fallback to all manufacturers
            mfrMap.set(shackleCat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
          }
        }
        
        // For R4-4 (Eye Bolt), load from samleliste tables (RUD manufacturer has models)
        const eyeBoltCat = filteredCategories.find(c => c.code === 'R4-4')
        console.log('Eye Bolt category found:', eyeBoltCat)
        
        if (eyeBoltCat) {
          // Get the samleliste_categories ID for R4-4 (Eye Bolt in samleliste)
          const { data: eyeBoltSamlelisteCatData, error: eyeBoltSamlelisteCatError } = await supabase
            .from('samleliste_categories')
            .select('id, code')
            .eq('code', 'R4-4')
            .single()
          
          console.log('Samleliste category for R4-4:', { eyeBoltSamlelisteCatData, eyeBoltSamlelisteCatError })
          
          if (eyeBoltSamlelisteCatData) {
            // Store the mapping from categories_v2 ID to samleliste_categories ID
            samlelisteCatMap.set(eyeBoltCat.id, eyeBoltSamlelisteCatData.id)
            console.log('Eye Bolt category mapping set:', eyeBoltCat.id, '->', eyeBoltSamlelisteCatData.id)
            
            // Load manufacturers using the samleliste category ID
            const { data: eyeBoltMfrs } = await supabase
              .from('samleliste_category_manufacturers')
              .select(`
                manufacturer:samleliste_manufacturers(id, name, short_name)
              `)
              .eq('category_id', eyeBoltSamlelisteCatData.id)
              .order('is_primary', { ascending: false })
            
            if (eyeBoltMfrs && eyeBoltMfrs.length > 0) {
              mfrMap.set(eyeBoltCat.id, eyeBoltMfrs.map((m: any) => m.manufacturer).filter(Boolean))
            } else if (allMfrs) {
              // Fallback to all manufacturers if no samleliste config
              mfrMap.set(eyeBoltCat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
            }
          } else if (allMfrs) {
            // No samleliste entry found - fallback to all manufacturers
            mfrMap.set(eyeBoltCat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
          }
        }
        
        // For R4-3 (D-Sjakkel), load from samleliste tables
        const dShackleCat = filteredCategories.find(c => c.code === 'R4-3')
        console.log('D-Sjakkel category found:', dShackleCat)
        
        if (dShackleCat) {
          // Get the samleliste_categories ID for R4-3 (D-Sjakkel in samleliste)
          const { data: dShackleSamlelisteCatData, error: dShackleSamlelisteCatError } = await supabase
            .from('samleliste_categories')
            .select('id, code')
            .eq('code', 'R4-3')
            .single()
          
          console.log('Samleliste category for R4-3:', { dShackleSamlelisteCatData, dShackleSamlelisteCatError })
          
          if (dShackleSamlelisteCatData) {
            // Store the mapping from categories_v2 ID to samleliste_categories ID
            samlelisteCatMap.set(dShackleCat.id, dShackleSamlelisteCatData.id)
            console.log('D-Sjakkel category mapping set:', dShackleCat.id, '->', dShackleSamlelisteCatData.id)
            
            // Load manufacturers using the samleliste category ID
            const { data: dShackleMfrs } = await supabase
              .from('samleliste_category_manufacturers')
              .select(`
                manufacturer:samleliste_manufacturers(id, name, short_name)
              `)
              .eq('category_id', dShackleSamlelisteCatData.id)
              .order('is_primary', { ascending: false })
            
            if (dShackleMfrs && dShackleMfrs.length > 0) {
              mfrMap.set(dShackleCat.id, dShackleMfrs.map((m: any) => m.manufacturer).filter(Boolean))
            } else if (allMfrs) {
              // Fallback to all manufacturers if no samleliste config
              mfrMap.set(dShackleCat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
            }
          } else if (allMfrs) {
            // No samleliste entry found - fallback to all manufacturers
            mfrMap.set(dShackleCat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
          }
        }
        
        setSamlelisteCategoryMap(samlelisteCatMap)
        
        // For other categories, use manufacturers_categories table from database
        // Load all manufacturer-category relationships
        const { data: mfrCatData } = await supabase
          .from('manufacturers_categories')
          .select(`
            category_id,
            manufacturer:manufacturers_v2(id, name, short_name)
          `)
          .order('is_primary', { ascending: false })
        
        // Build a map of category_id -> manufacturer IDs
        const categoryMfrMap = new Map<string, Set<string>>()
        if (mfrCatData) {
          for (const mc of mfrCatData) {
            if (!mc.manufacturer) continue
            if (!categoryMfrMap.has(mc.category_id)) {
              categoryMfrMap.set(mc.category_id, new Set())
            }
            categoryMfrMap.get(mc.category_id)!.add((mc.manufacturer as any).id)
          }
        }
        
        if (allMfrs) {
          // Load manufacturers for filtered categories (the main dropdown)
          for (const cat of filteredCategories) {
            if (cat.code === 'R4-2' || cat.code === 'R4-3' || cat.code === 'R4-4') continue // Already handled above with samleliste system
            
            // Check if this category has manufacturers defined in manufacturers_categories
            // Also check parent category (e.g., R1-1 should use R1 manufacturers)
            const parentCatId = allCatsData?.find(c => c.code === cat.code.split('-')[0])?.id
            
            let allowedMfrIds = categoryMfrMap.get(cat.id)
            if (!allowedMfrIds && parentCatId) {
              // Use parent category's manufacturers
              allowedMfrIds = categoryMfrMap.get(parentCatId)
            }
            
            if (allowedMfrIds && allowedMfrIds.size > 0) {
              // Filter manufacturers by IDs from database
              const filteredMfrs = allMfrs
                .filter(m => allowedMfrIds!.has(m.id))
                .map(m => ({ id: m.id, name: m.name, short_name: m.short_name }))
              
              mfrMap.set(cat.id, filteredMfrs)
            } else {
              // No mapping defined - show all manufacturers
              mfrMap.set(cat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
            }
          }
          
          // Also load manufacturers for ALL categories (for "Tilpasset" custom selection)
          for (const cat of allCatsData) {
            if (mfrMap.has(cat.id)) continue // Already handled above
            
            // Check if this category has manufacturers defined in manufacturers_categories
            const parentCatId = allCatsData?.find(c => c.code === cat.code.split('-')[0])?.id
            
            let allowedMfrIds = categoryMfrMap.get(cat.id)
            if (!allowedMfrIds && parentCatId) {
              allowedMfrIds = categoryMfrMap.get(parentCatId)
            }
            
            if (allowedMfrIds && allowedMfrIds.size > 0) {
              const filteredMfrs = allMfrs
                .filter(m => allowedMfrIds!.has(m.id))
                .map(m => ({ id: m.id, name: m.name, short_name: m.short_name }))
              mfrMap.set(cat.id, filteredMfrs)
            } else {
              // No mapping - show all manufacturers
              mfrMap.set(cat.id, allMfrs.map(m => ({ id: m.id, name: m.name, short_name: m.short_name })))
            }
          }
        }
        
        setAllManufacturers(mfrMap)
      }
      
      // Load company info
      const { data: companyData } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single()
      
      if (companyData) setCompanyName(companyData.name)
      
      // Load current user
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        // Get user profile with role info and MT qualification
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email, role, supervisor, signature, mt, mt_level')
          .eq('id', user.id)
          .single()
        
        const isAdmin = profile?.role === 'admin' || profile?.supervisor === true
        
        // Check if user has MT qualification (boolean mt=true OR mt_level > 0)
        const hasMt = profile?.mt === true || (profile?.mt_level && profile.mt_level > 0)
        setUserHasMtQualification(!!hasMt)
        
        setCurrentUser({
          id: user.id,
          name: profile?.full_name || profile?.email || user.email || 'Ukjent',
          isAdmin,
          signature: profile?.signature || undefined
        })
        
        // Load inspectors (for admins to select who performed) - include MT qualification
        const { data: inspectorProfiles } = await supabase
          .from('profiles')
          .select('id, full_name, signature, mt, mt_level')
          .eq('role', 'inspector')
          .order('full_name')
        
        if (inspectorProfiles) {
          setInspectors(inspectorProfiles.map(p => ({
            id: p.id,
            name: p.full_name || 'Ukjent',
            signature: p.signature || undefined,
            hasMtQualification: p.mt === true || (p.mt_level && p.mt_level > 0)
          })))
        }
        
        // Load admins/supervisors (for inspectors to select who is responsible)
        const { data: supervisorProfiles } = await supabase
          .from('profiles')
          .select('id, full_name, signature')
          .or('role.eq.admin,supervisor.eq.true')
          .order('full_name')
        
        if (supervisorProfiles) {
          setSupervisors(supervisorProfiles.map(p => ({
            id: p.id,
            name: p.full_name || 'Ukjent',
            signature: p.signature || undefined
          })))
        }
      }
    } catch {
      // Failed to load data
    } finally {
      setLoading(false)
    }
  }

  // Load product types when manufacturer is selected
  // For shackles, uses samleliste_categories ID from samlelisteCategoryMap
  const loadProductTypes = async (categoryId: string, manufacturerId: string) => {
    const key = `${categoryId}-${manufacturerId}`
    
    // Skip if already loaded (but log for debugging)
    if (allProductTypes.has(key)) {
      console.log('loadProductTypes: already cached for key:', key)
      return
    }
    
    // For shackles (R4-2), use the samleliste category ID
    // The map links categories_v2.id → samleliste_categories.id
    const samlelisteCatId = samlelisteCategoryMap.get(categoryId)
    
    console.log('loadProductTypes called:', {
      categoryId,
      manufacturerId,
      samlelisteCatId,
      mapSize: samlelisteCategoryMap.size,
      mapEntries: Array.from(samlelisteCategoryMap.entries())
    })
    
    // If no mapping found, this category doesn't have product types in samleliste
    if (!samlelisteCatId) {
      console.log('loadProductTypes: No samleliste category mapping found for:', categoryId)
      return
    }
    
    const { data, error } = await supabase
      .from('samleliste_product_types')
      .select('id, type_name, type_code, description, default_specs, wll_options, dimension_options, standard, grade')
      .eq('category_id', samlelisteCatId)
      .eq('manufacturer_id', manufacturerId)
      .eq('is_active', true)
      .order('sort_order')
    
    console.log('samleliste_product_types result:', { data, error, count: data?.length })
    
    if (data && data.length > 0) {
      setAllProductTypes(prev => new Map(prev).set(key, data))
    } else {
      console.log('loadProductTypes: No product types found for category:', samlelisteCatId, 'manufacturer:', manufacturerId)
    }
  }

  // Load spec templates for a custom category
  const loadCustomSpecTemplates = async (categoryId: string) => {
    // Skip if already loaded
    if (customSpecTemplates.has(categoryId)) {
      console.log('Custom spec templates already loaded for category:', categoryId)
      return customSpecTemplates.get(categoryId) || []
    }
    
    console.log('Loading custom spec templates for category:', categoryId)
    
    try {
      // Try specification_templates_with_categories view first
      let { data: templates, error: templatesError } = await supabase
        .from('specification_templates_with_categories')
        .select('*')
        .eq('category_id', categoryId)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })

      // Fallback to category_specification_templates_v2 with canonical join
      if (templatesError || !templates || templates.length === 0) {
        const { data: mappings } = await supabase
          .from('category_specification_templates_v2')
          .select(`
            *,
            template:canonical_specification_templates(*)
          `)
          .eq('category_id', categoryId)
          .eq('is_visible', true)
          .order('display_order', { ascending: true })

        if (mappings && mappings.length > 0) {
          templates = mappings.map((m: any) => ({
            ...m.template,
            category_id: m.category_id,
            is_required: m.is_required,
            display_order: m.display_order
          }))
        }
      }

      // Load unit symbols
      if (templates && templates.length > 0) {
        const unitIds = templates.map((t: any) => t.default_unit_id).filter(Boolean)
        if (unitIds.length > 0) {
          const { data: units } = await supabase
            .from('units_v2')
            .select('id, symbol')
            .in('id', unitIds)
          
          if (units) {
            const unitMap = Object.fromEntries(units.map(u => [u.id, u.symbol]))
            templates = templates.map((t: any) => ({
              ...t,
              unit_symbol: t.default_unit_id ? unitMap[t.default_unit_id] : undefined
            }))
          }
        }
      }

      const specTemplates: CustomSpecTemplate[] = (templates || []).map((t: any) => ({
        id: t.id,
        spec_key: t.spec_key,
        label_no: t.label_no,
        label_en: t.label_en,
        data_type: t.data_type || 'text',
        is_required: t.is_required || false,
        display_order: t.display_order || 0,
        unit_symbol: t.unit_symbol,
        default_value: t.default_value
      }))

      console.log('Loaded spec templates:', specTemplates.length, 'for category:', categoryId)
      setCustomSpecTemplates(prev => new Map(prev).set(categoryId, specTemplates))
      return specTemplates
    } catch (err) {
      console.error('Error loading custom spec templates:', err)
      return []
    }
  }

  // Get custom spec templates for a category (sorted by display_order)
  const getCustomSpecTemplates = (categoryId: string) => {
    const templates = customSpecTemplates.get(categoryId) || []
    // Sort by display_order to ensure correct order
    return [...templates].sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
  }

  // Get display name for a category (without code)
  const getCategoryDisplayName = (cat: SamlelisteCategory) => 
    CATEGORY_DISPLAY_NAMES[cat.code] || cat.name_no || cat.name_en || cat.code

  // Filter categories based on search query (excluding parent categories)
  const getFilteredCustomCategories = (searchQuery: string) => {
    // Get all parent IDs (categories that have children)
    const parentIds = new Set(allAvailableCategories.map(c => c.subcategory_id).filter(Boolean))
    
    // Filter to only leaf categories (not parents)
    const leafCategories = allAvailableCategories.filter(c => !parentIds.has(c.id))
    
    if (!searchQuery.trim()) return leafCategories
    const query = searchQuery.toLowerCase()
    return leafCategories.filter(c => {
      const displayName = getCategoryDisplayName(c).toLowerCase()
      const nameNo = (c.name_no || '').toLowerCase()
      const nameEn = (c.name_en || '').toLowerCase()
      return displayName.includes(query) || nameNo.includes(query) || nameEn.includes(query)
    })
  }

  // Get category by ID (looks in both filtered categories and all available categories)
  const getCategory = (categoryId: string) => 
    categories.find(c => c.id === categoryId) || allAvailableCategories.find(c => c.id === categoryId)
  
  // Effective MT qualification - true if current user has MT cert, or if admin selected inspector with MT cert
  const effectiveMtQualification = (() => {
    // If current user has MT qualification, they can generate NDT reports
    if (userHasMtQualification) return true
    
    // If admin and has selected an inspector with MT qualification
    if (currentUser?.isAdmin && selectedInspectorId) {
      const selectedInspector = inspectors.find(i => i.id === selectedInspectorId)
      return selectedInspector?.hasMtQualification || false
    }
    
    return false
  })()
  
  // Get manufacturers for category
  const getManufacturersForCategory = (categoryId: string) => allManufacturers.get(categoryId) || []
  
  // Get product types for category+manufacturer
  const getProductTypes = (categoryId: string, manufacturerId: string) => 
    allProductTypes.get(`${categoryId}-${manufacturerId}`) || []

  // Get filtered product types based on shackle type
  const getFilteredProductTypes = (categoryId: string, manufacturerId: string, shackleType?: ShackleType) => {
    const allTypes = getProductTypes(categoryId, manufacturerId)
    if (!shackleType || !isShackleCategory(categoryId)) return allTypes
    
    if (shackleType === 'offshore') {
      // Filter out D-Sjakkel models for Offshore
      return allTypes.filter(pt => 
        !pt.type_code?.includes('D-SHACKLE') && 
        !pt.type_name?.toLowerCase().includes('d-sjakkel')
      )
    } else if (shackleType === 'd-sjakkel') {
      // Only show D-Sjakkel models
      return allTypes.filter(pt => 
        pt.type_code?.includes('D-SHACKLE') || 
        pt.type_name?.toLowerCase().includes('d-sjakkel')
      )
    }
    return allTypes
  }

  // Get product type by ID
  const getProductType = (categoryId: string, manufacturerId: string, productTypeId: string) => {
    const types = getProductTypes(categoryId, manufacturerId)
    return types.find(t => t.id === productTypeId)
  }

  // Get WLL options for selected product type
  const getWllOptions = (categoryId: string, manufacturerId: string, productTypeId: string) => {
    const productType = getProductType(categoryId, manufacturerId, productTypeId)
    return productType?.wll_options || []
  }

  // Get dimension for selected WLL
  const getDimensionForWll = (categoryId: string, manufacturerId: string, productTypeId: string, wll: string) => {
    const productType = getProductType(categoryId, manufacturerId, productTypeId)
    return productType?.dimension_options?.[wll] || null
  }

  // Universal thread options for eye bolts (metric M-series and imperial UNC/UNF)
  const UNIVERSAL_THREAD_OPTIONS = [
    // Metric coarse threads (sorted by size)
    'M6', 'M8', 'M10', 'M12', 'M14', 'M16', 'M18', 'M20', 'M22', 'M24', 
    'M27', 'M30', 'M33', 'M36', 'M39', 'M42', 'M45', 'M48', 'M56', 'M64',
    // Metric fine threads (sorted by size)
    'M8x1', 'M12x1.5', 'M16x1.5', 'M20x2', 'M24x2', 'M30x2', 'M36x3',
    // Imperial UNC/UNF threads (sorted by size)
    '1/4"-20UNC', '5/16"-18UNC', '3/8"-16UNC', '7/16"-14UNC', '1/2"-13UNC',
    '5/8"-11UNC', '3/4"-16UNF', '3/4"-10UNC', '7/8"-9UNC', '1"-8UNC',
    '1 1/8"-8UN', '1 1/8"-7UNC', '1 1/4"-7UNC', '1 1/2"-6UNC', '1 3/4"-5UNC', '2"-4.5UNC'
  ]

  // Get all available thread options for eye bolts (universal list)
  const getThreadOptions = () => UNIVERSAL_THREAD_OPTIONS


  // Check if category has quantity options
  const hasQuantityOptions = (categoryId: string) => {
    const cat = getCategory(categoryId)
    return cat?.quantity_options && cat.quantity_options.length > 0
  }

  // Get quantity options for category
  const getQuantityOptions = (categoryId: string) => {
    const cat = getCategory(categoryId)
    return cat?.quantity_options || []
  }

  // Check if category needs batch ID fields (Offshore and H-Sjakkel, not D-Sjakkel)
  const needsBatchIds = (categoryId: string, entry?: SmartEquipmentEntry) => {
    const cat = getCategory(categoryId)
    // For shackle categories, check the shackle type
    if (entry && isShackleCategory(categoryId)) {
      return shackleNeedsBatchIds(entry)
    }
    return cat?.has_batch_bow || cat?.has_batch_bolt
  }

  // Check if category is R4-2 (Shackle) - has product type/model selection
  const isShackleCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    return cat?.code === 'R4-2'
  }
  
  // Check if entry is D-Sjakkel type (based on shackleType selection)
  const isDShackleType = (entry: SmartEquipmentEntry) => {
    return isShackleCategory(entry.category_id) && entry.shackleType === 'd-sjakkel'
  }
  
  // Check if entry is H-Sjakkel type (grade + manual WLL + batch IDs, no model)
  const isHSjakkelType = (entry: SmartEquipmentEntry) => {
    return isShackleCategory(entry.category_id) && entry.shackleType === 'h-sjakkel'
  }
  
  // Check if entry is Offshore Sjakkel type (uses model dropdown, WLL dropdown, batch IDs)
  const isOffshoreSjakkelType = (entry: SmartEquipmentEntry) => {
    return isShackleCategory(entry.category_id) && entry.shackleType === 'offshore'
  }
  
  // Check if entry needs batch IDs (all shackle types: Offshore, D-Sjakkel, H-Sjakkel)
  const shackleNeedsBatchIds = (entry: SmartEquipmentEntry) => {
    return isOffshoreSjakkelType(entry) || isDShackleType(entry) || isHSjakkelType(entry)
  }
  
  // Check if category is R4-4 (Eye Bolt/Øyebolt) - has model selection for RUD manufacturer
  const isEyeBoltCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    return cat?.code === 'R4-4'
  }

  // Check if category is R2 or R2-x (Fiber Sling) - needs length input
  const isFiberSlingCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    return cat?.code?.startsWith('R2')
  }
  
  // Check if category is R4-3 (D-Sjakkel) - requires grade selection and manual WLL
  // Also checks shackleType for merged Sjakkel category
  const isDShackleCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    return cat?.code === 'R4-3'
  }
  
  // Check if entry needs D-Sjakkel logic (either R4-3 category or D-Sjakkel shackle type)
  const needsDShackleLogic = (entry: SmartEquipmentEntry) => {
    return isDShackleCategory(entry.category_id) || isDShackleType(entry)
  }
  
  // Legacy function for backward compatibility - now checks both category and shackle type
  const isDShackle = (categoryId: string, _manufacturerId?: string, _productTypeId?: string, entry?: SmartEquipmentEntry) => {
    if (entry) return needsDShackleLogic(entry)
    return isDShackleCategory(categoryId)
  }
  
  // D-sjakkel always uses manual WLL input (no predefined options)
  const isDShackleWithoutWllOptions = (categoryId: string, _manufacturerId?: string, _productTypeId?: string, entry?: SmartEquipmentEntry) => {
    if (entry) return needsDShackleLogic(entry)
    return isDShackleCategory(categoryId)
  }
  
  // Check if manufacturer is RUD (for eye bolt model dropdown)
  const isRudManufacturer = (manufacturerId: string) => {
    // Search through all manufacturer lists to find by ID
    for (const [, mfrs] of allManufacturers) {
      const mfr = mfrs.find(m => m.id === manufacturerId)
      if (mfr && mfr.name.toUpperCase().includes('RUD')) {
        return true
      }
    }
    return false
  }
  
  // Check if category is R1 (chain slings) - can generate MT/MPI report
  const isChainSlingCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    if (!cat) return false
    return matchesCategoryFamily(cat.code, 'R1')
  }
  
  // Check if category is a sling type that needs part selection (R1 or R3)
  const isSlingCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    if (!cat) return false
    return cat.code === 'R1' || cat.code === 'R3'
  }
  
  // Check if category is FAL (Fall Protection) - uses Kapasitet (kg) instead of WLL
  const isFallProtectionCategory = (categoryId: string) => {
    const cat = getCategory(categoryId)
    if (!cat) return false
    return cat.code.startsWith('FAL')
  }
  
  // Check if category should show Antall/quantity field (NOT for slings R1, R2, R3)
  const shouldShowQuantity = (categoryId: string) => {
    const cat = getCategory(categoryId)
    if (!cat) return true // Default to showing
    // Hide quantity for all sling categories (R1 chain, R2 textile, R3 wire)
    return !matchesAnyCategoryFamily(cat.code, ['R1', 'R2', 'R3'])
  }

  // Build combined serial number
  const buildSerialNumber = (entry: SmartEquipmentEntry) => {
    // For categories with batch IDs (like offshore shackles), contract batch IDs into serial number
    if (needsBatchIds(entry.category_id) && entry.batch_id_bow && entry.batch_id_bolt) {
      return `${entry.batch_id_bow}-${entry.batch_id_bolt}`
    }
    return entry.serial_number
  }

  // Search for existing equipment
  const searchExistingEquipment = useCallback(async (entryId: string, query: string) => {
    if (!companyId || !query.trim() || query.length < 2) {
      setEquipmentSearchResults(prev => ({ ...prev, [entryId]: [] }))
      return
    }
    
    console.log('🔍 Searching for:', query, 'in company:', companyId)
    setIsSearching(prev => ({ ...prev, [entryId]: true }))
    
    try {
      // Search equipment by name, serial number, or asset number
      // Note: Search ALL equipment in company (not restricted to samleliste categories)
      const searchTerm = `%${query.trim()}%`
      
      const { data, error } = await supabase
        .from('equipment_v2')
        .select(`
          id,
          name,
          serial_number,
          asset_number,
          category_id,
          manufacturer_id,
          last_ndt_date,
          category:categories_v2(id, code, title_en)
        `)
        .eq('company_id', companyId)
        .or(`name.ilike.${searchTerm},serial_number.ilike.${searchTerm},asset_number.ilike.${searchTerm}`)
        .limit(10)
      
      if (error) {
        console.error('🔍 Supabase query error:', error)
        throw error
      }
      
      console.log('🔍 Search found', data?.length || 0, 'results')
      
      // Collect manufacturer IDs to fetch names
      const manufacturerIds = (data || [])
        .map((eq: any) => eq.manufacturer_id)
        .filter((id: string | null) => id)
      
      // Fetch manufacturer names from manufacturers_v2
      let manufacturerMap: Record<string, string> = {}
      if (manufacturerIds.length > 0) {
        const { data: mfrData } = await supabase
          .from('manufacturers_v2')
          .select('id, name')
          .in('id', manufacturerIds)
        
        if (mfrData) {
          for (const mfr of mfrData) {
            manufacturerMap[mfr.id] = mfr.name
          }
        }
      }
      
      // Fetch ALL specifications for each equipment
      const equipmentIds = (data || []).map((eq: any) => eq.id)
      let specsMap: Record<string, Record<string, any>> = {}
      let allSpecsMap: Record<string, SpecDisplayItem[]> = {}
      
      if (equipmentIds.length > 0) {
        // Fetch all specs (simple query without complex joins)
        const { data: specsData, error: specsError } = await supabase
          .from('equipment_specifications_v2')
          .select('equipment_id, spec_key, template_id, unit_id, value_text, value_numeric, value_integer, value_boolean, value_reference_id')
          .in('equipment_id', equipmentIds)
        
        if (specsError) {
          console.error('Error fetching specs:', specsError)
        }
        
        // Get unique template IDs and unit IDs to fetch labels
        const templateIds = new Set<string>()
        const unitIds = new Set<string>()
        const referenceIds = new Set<string>()
        
        if (specsData) {
          for (const spec of specsData) {
            if (spec.template_id) templateIds.add(spec.template_id)
            if (spec.unit_id) unitIds.add(spec.unit_id)
            // Collect ALL potential UUIDs for resolution
            if (spec.value_reference_id) {
              referenceIds.add(spec.value_reference_id)
            }
            // Also check value_text for UUIDs (legacy data or standards)
            if (spec.value_text && spec.value_text.length === 36 && spec.value_text.includes('-')) {
              referenceIds.add(spec.value_text)
            }
          }
        }
        
        // Fetch templates for labels and reference table info
        let templateMap: Record<string, { label_no: string; label_en: string; group_order: number; reference_table?: string }> = {}
        if (templateIds.size > 0) {
          const { data: templateData } = await supabase
            .from('canonical_specification_templates')
            .select('id, label_no, label_en, group_order, reference_table')
            .in('id', Array.from(templateIds))
          
          if (templateData) {
            for (const t of templateData) {
              templateMap[t.id] = { 
                label_no: t.label_no, 
                label_en: t.label_en, 
                group_order: t.group_order ?? 999,
                reference_table: t.reference_table || undefined
              }
            }
          }
        }
        
        // Fetch units for symbols
        let unitMap: Record<string, string> = {}
        if (unitIds.size > 0) {
          const { data: unitData } = await supabase
            .from('units_v2')
            .select('id, symbol')
            .in('id', Array.from(unitIds))
          
          if (unitData) {
            for (const u of unitData) {
              unitMap[u.id] = u.symbol
            }
          }
        }
        
        // Resolve reference IDs (manufacturers, standards, etc.)
        let referenceMap: Record<string, string> = { ...manufacturerMap }
        if (referenceIds.size > 0) {
          const refIdArray = Array.from(referenceIds)
          
          // Fetch from manufacturers_v2
          const { data: mfrData } = await supabase
            .from('manufacturers_v2')
            .select('id, name')
            .in('id', refIdArray)
          
          if (mfrData) {
            for (const mfr of mfrData) {
              referenceMap[mfr.id] = mfr.name
            }
          }
          
          // Fetch from standards_v2
          const { data: stdData } = await supabase
            .from('standards_v2')
            .select('id, code, title')
            .in('id', refIdArray)
          
          if (stdData) {
            for (const std of stdData) {
              // Use code as the display value (e.g., "EN 13414-1")
              referenceMap[std.id] = std.code || std.title || std.id
            }
          }
        }
        
        // Helper to check if value is a UUID
        const isUUID = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)
        
        // Second pass: collect any unresolved UUIDs for prod_acc_to specifically
        const unresolvedStandardIds = new Set<string>()
        if (specsData) {
          for (const spec of specsData) {
            if (spec.spec_key === 'prod_acc_to') {
              const val = spec.value_reference_id || spec.value_text
              if (val && isUUID(val) && !referenceMap[val]) {
                unresolvedStandardIds.add(val)
              }
            }
          }
        }
        
        // Try to resolve any remaining standard UUIDs
        if (unresolvedStandardIds.size > 0) {
          const { data: moreStdData } = await supabase
            .from('standards_v2')
            .select('id, code, title')
            .in('id', Array.from(unresolvedStandardIds))
          
          if (moreStdData) {
            for (const std of moreStdData) {
              referenceMap[std.id] = std.code || std.title || std.id
            }
          }
        }
        
        // Build specsMap and allSpecsMap
        if (specsData) {
          for (const spec of specsData) {
            if (!specsMap[spec.equipment_id]) specsMap[spec.equipment_id] = {}
            
            const template = spec.template_id ? templateMap[spec.template_id] : null
            const unitSymbol = spec.unit_id ? unitMap[spec.unit_id] : null
            
            specsMap[spec.equipment_id][spec.spec_key] = {
              value_text: spec.value_text,
              value_numeric: spec.value_numeric,
              value_integer: spec.value_integer,
              value_boolean: spec.value_boolean,
              value_reference_id: spec.value_reference_id
            }
          }
          
          // Build display specs for each equipment
          for (const eqId of equipmentIds) {
            const displaySpecs: SpecDisplayItem[] = []
            const eqSpecsRaw = specsData.filter(s => s.equipment_id === eqId)
            
            for (const spec of eqSpecsRaw) {
              const template = spec.template_id ? templateMap[spec.template_id] : null
              const unitSymbol = spec.unit_id ? unitMap[spec.unit_id] : null
              
              // Get label (prefer our mapping, then template labels, then spec_key)
              const label = SPEC_DISPLAY_LABELS[spec.spec_key] || template?.label_no || template?.label_en || spec.spec_key
              
              // Get value (without unit - we'll add unit_symbol separately for editing)
              let value = ''
              if (spec.value_reference_id) {
                // Try to resolve reference ID
                value = referenceMap[spec.value_reference_id] || spec.value_reference_id
              } else if (spec.value_text) {
                if (isUUID(spec.value_text)) {
                  // Try to resolve UUID from referenceMap
                  value = referenceMap[spec.value_text] || spec.value_text
                } else {
                  value = spec.value_text
                }
              } else if (spec.value_numeric !== null && spec.value_numeric !== undefined) {
                value = String(spec.value_numeric)
              } else if (spec.value_integer !== null && spec.value_integer !== undefined) {
                value = String(spec.value_integer)
              } else if (spec.value_boolean !== null && spec.value_boolean !== undefined) {
                value = spec.value_boolean ? 'Yes' : 'No'
              }
              
              // If value is still a UUID after resolution, try to hide it or show placeholder
              if (isUUID(value)) {
                console.warn(`Unresolved UUID for spec ${spec.spec_key}: ${value}`)
                // For prod_acc_to, show as unresolved
                if (spec.spec_key === 'prod_acc_to') {
                  value = '(Ikke oppløst/Unresolved)'
                }
              }
              
              if (value) {
                displaySpecs.push({
                  spec_key: spec.spec_key,
                  label,
                  value,
                  priority: template?.group_order ?? 999,
                  unit_symbol: unitSymbol || undefined
                })
              }
            }
            
            // Sort by priority
            displaySpecs.sort((a, b) => a.priority - b.priority)
            allSpecsMap[eqId] = displaySpecs
          }
        }
        
        // Update manufacturerMap with resolved references
        manufacturerMap = { ...manufacturerMap, ...referenceMap }
      }
      
      const results: ExistingEquipment[] = (data || []).map((eq: any) => {
        const specs = specsMap[eq.id] || {}
        let allSpecs = [...(allSpecsMap[eq.id] || [])]
        
        // Check if equipment category should show NDT date
        // NDT is required for: Chain slings (R1), Wire slings (R3), F categories (lifting equipment)
        const categoryCode = eq.category?.code || ''
        const shouldShowNDT = categoryCode.startsWith('R1') || 
                              categoryCode.startsWith('R2') || 
                              categoryCode.startsWith('R3') || 
                              categoryCode.startsWith('F') ||
                              categoryCode.startsWith('G')
        
        // Add NDT date field for categories that require it (even if empty)
        if (shouldShowNDT && !allSpecs.some(s => s.spec_key === 'last_ndt_date' || s.spec_key === 'ndt_date')) {
          let formattedDate = '-'
          if (eq.last_ndt_date) {
            const ndtDate = new Date(eq.last_ndt_date)
            formattedDate = ndtDate.toLocaleDateString('nb-NO', { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit' 
            })
          }
          allSpecs.push({
            spec_key: 'last_ndt_date',
            label: 'Siste NDT/Last NDT',
            value: formattedDate,
            priority: 50 // Show after most specs but not at the end
          })
          // Re-sort after adding
          allSpecs.sort((a, b) => a.priority - b.priority)
        }
        
        // Get manufacturer name: first from equipment_v2.manufacturer_id, then from specs
        let manufacturerName: string | null = null
        if (eq.manufacturer_id && manufacturerMap[eq.manufacturer_id]) {
          manufacturerName = manufacturerMap[eq.manufacturer_id]
        } else {
          const mfrSpec = specs['manufacturer']
          if (mfrSpec) {
            if (mfrSpec.value_reference_id && manufacturerMap[mfrSpec.value_reference_id]) {
              manufacturerName = manufacturerMap[mfrSpec.value_reference_id]
            } else if (mfrSpec.value_text) {
              // Check if UUID or direct name
              if (mfrSpec.value_text.length === 36 && mfrSpec.value_text.includes('-')) {
                manufacturerName = manufacturerMap[mfrSpec.value_text] || null
              } else {
                manufacturerName = mfrSpec.value_text
              }
            }
          }
        }
        
        // Get WLL value
        const wllSpec = specs['wll']
        const wllValue = wllSpec?.value_numeric ?? wllSpec?.value_text
        
        // Get dimension value  
        const dimSpec = specs['dim_mm']
        const dimValue = dimSpec?.value_numeric ?? dimSpec?.value_text
        
        // Get resolved standard value from allSpecs (already has UUID resolved)
        const resolvedStandard = allSpecs.find(s => s.spec_key === 'prod_acc_to')?.value || null
        
        // Get quantity from specs (check multiple possible keys)
        const qtySpec = specs['quantity'] || specs['qty'] || specs['antall']
        
        // Debug: log all spec keys to find quantity key
        console.log(`📦 Equipment ${eq.name} spec keys:`, Object.keys(specs))
        console.log(`📦 Equipment ${eq.name} allSpecs:`, allSpecs.map(s => `${s.spec_key}=${s.value}`).join(', '))
        
        // Also check allSpecs for quantity (might have different key)
        const qtyFromAllSpecs = allSpecs.find(s => 
          s.spec_key === 'quantity' || 
          s.spec_key === 'qty' || 
          s.spec_key === 'antall' ||
          s.label?.toLowerCase().includes('antall') ||
          s.label?.toLowerCase().includes('quantity')
        )
        
        // Use spec value or allSpecs value
        let quantityValue: number | null = null
        if (qtySpec) {
          quantityValue = qtySpec.value_numeric ?? qtySpec.value_integer ?? (qtySpec.value_text ? parseInt(qtySpec.value_text) : null)
        } else if (qtyFromAllSpecs) {
          quantityValue = parseInt(qtyFromAllSpecs.value) || null
          console.log(`📦 Found quantity in allSpecs: ${qtyFromAllSpecs.spec_key} = ${qtyFromAllSpecs.value}`)
        }
        
        return {
          id: eq.id,
          name: eq.name,
          serial_number: eq.serial_number || '',
          asset_number: eq.asset_number || '',
          category_id: eq.category_id || '',
          category_code: eq.category?.code || '',
          category_name: CATEGORY_DISPLAY_NAMES[eq.category?.code] || eq.category?.title_en || '',
          manufacturer_id: eq.manufacturer_id || null,
          manufacturer_name: manufacturerName,
          allSpecs, // All specs with labels for display
          wll: wllValue ? `${wllValue}t` : null,
          dimension: dimValue ? `${dimValue} mm` : null,
          batch_id_bow: specs['batch_id_bow']?.value_text || null,
          batch_id_bolt: specs['batch_id_bolt']?.value_text || null,
          standard: resolvedStandard, // Use resolved value from allSpecs
          grade: specs['grade']?.value_text || null,
          quantity: quantityValue // Antall for display
        }
      })
      
      setEquipmentSearchResults(prev => ({ ...prev, [entryId]: results }))
    } catch (err) {
      console.error('Equipment search failed:', err)
      setEquipmentSearchResults(prev => ({ ...prev, [entryId]: [] }))
    } finally {
      setIsSearching(prev => ({ ...prev, [entryId]: false }))
    }
  }, [companyId])

  // Debounced search handler
  const handleEquipmentSearch = useCallback((entryId: string, query: string) => {
    setEquipmentSearchQuery(prev => ({ ...prev, [entryId]: query }))
    
    // Clear previous timeout for this entry
    if (searchTimeoutRef.current[entryId]) {
      clearTimeout(searchTimeoutRef.current[entryId])
    }
    
    // Debounce search
    searchTimeoutRef.current[entryId] = setTimeout(() => {
      searchExistingEquipment(entryId, query)
    }, 300)
  }, [searchExistingEquipment])

  // Clear search for an entry
  const clearEquipmentSearch = useCallback((entryId: string) => {
    setEquipmentSearchQuery(prev => ({ ...prev, [entryId]: '' }))
    setEquipmentSearchResults(prev => ({ ...prev, [entryId]: [] }))
  }, [])

  // Entry management
  const updateEntry = useCallback((id: string, updates: Partial<SmartEquipmentEntry>) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id !== id) return entry
      
      const updated = { ...entry, ...updates, status: 'pending' as const, errors: [] }
      
      // When category changes, reset fields that weren't explicitly provided
      if (updates.category_id && updates.category_id !== entry.category_id) {
        if (!('manufacturer_id' in updates)) updated.manufacturer_id = ''
        if (!('product_type_id' in updates)) updated.product_type_id = ''
        if (!('name' in updates)) updated.name = ''
        if (!('batch_id_bow' in updates)) updated.batch_id_bow = ''
        if (!('batch_id_bolt' in updates)) updated.batch_id_bolt = ''
        if (!('serial_number' in updates)) updated.serial_number = ''
        if (!('wll' in updates)) updated.wll = ''
        if (!('dimension' in updates)) updated.dimension = ''
        if (!('standard' in updates)) updated.standard = ''
        if (!('grade' in updates)) updated.grade = ''
      }
      
      // When manufacturer changes (and not from existing equipment selection), reset product type
      // Note: We no longer reset the name here - it's set by the Utstyrstype selection
      if (updates.manufacturer_id && updates.manufacturer_id !== entry.manufacturer_id && !updates.existing_equipment_id) {
        if (!('product_type_id' in updates)) updated.product_type_id = ''
        if (updated.category_id && updates.manufacturer_id) {
          loadProductTypes(updated.category_id, updates.manufacturer_id)
        }
      }
      
      return updated
    }))
  }, [allProductTypes])

  // Select existing equipment - store its details directly (no category/manufacturer matching)
  const selectExistingEquipment = useCallback((entryId: string, equipment: ExistingEquipment) => {
    // For existing equipment, we store the spec values directly
    // No need to match samleliste categories/manufacturers - equipment already exists
    
    // Extract quantity from allSpecs (for inventory split detection)
    const quantitySpec = equipment.allSpecs.find(s => s.spec_key === 'quantity' || s.spec_key === 'qty' || s.spec_key === 'antall')
    const originalQuantity = quantitySpec ? parseInt(quantitySpec.value) || 1 : 1
    
    // Preload product types for this equipment's category/manufacturer
    // This ensures we can match product type during inventory split
    if (equipment.category_id && equipment.manufacturer_id) {
      loadProductTypes(equipment.category_id, equipment.manufacturer_id)
    }
    
    setEntries(prev => prev.map(entry => {
      if (entry.id !== entryId) return entry
      
      return {
        ...entry,
        // Store existing equipment reference
        existing_equipment_id: equipment.id,
        // Store display values from canonical specs
        name: equipment.name,
        serial_number: equipment.serial_number || '',
        wll: equipment.wll || '',
        dimension: equipment.dimension || '',
        batch_id_bow: equipment.batch_id_bow || '',
        batch_id_bolt: equipment.batch_id_bolt || '',
        standard: equipment.standard || '',
        grade: equipment.grade || '',
        // Store display names for showing in UI (not IDs)
        _existing_category_name: equipment.category_name,
        _existing_manufacturer_name: equipment.manufacturer_name,
        // Store all specs for display (like control card)
        _existing_all_specs: equipment.allSpecs,
        // Store original quantity for inventory split detection
        _original_quantity: originalQuantity,
        quantity: originalQuantity, // Pre-fill form with original quantity
        // Store original IDs for inventory split (when creating new equipment)
        _original_category_id: equipment.category_id,
        _original_manufacturer_id: equipment.manufacturer_id || undefined,
        // Clear samleliste IDs since this is existing equipment
        category_id: '',
        manufacturer_id: '',
        product_type_id: '',
        // Mark as valid for submission
        status: 'pending' as const,
        errors: []
      } as SmartEquipmentEntry
    }))
    
    // Clear search
    clearEquipmentSearch(entryId)
  }, [clearEquipmentSearch, loadProductTypes])

  // Toggle spec editing mode
  const toggleSpecEdit = useCallback((entryId: string, specKey: string) => {
    setEditingSpecs(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [specKey]: !prev[entryId]?.[specKey]
      }
    }))
  }, [])

  // Update a specific spec value for existing equipment
  const updateExistingSpec = useCallback((entryId: string, specKey: string, newValue: string) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id !== entryId || !entry._existing_all_specs) return entry
      
      // Update the spec in the _existing_all_specs array
      const updatedSpecs = entry._existing_all_specs.map(spec => 
        spec.spec_key === specKey ? { ...spec, value: newValue } : spec
      )
      
      // If editing quantity, also update entry.quantity for inventory split detection
      const updatedEntry: typeof entry = {
        ...entry,
        _existing_all_specs: updatedSpecs
      }
      
      if (specKey === 'quantity' || specKey === 'qty' || specKey === 'antall') {
        const parsedQuantity = parseInt(newValue) || 1
        updatedEntry.quantity = parsedQuantity
        console.log(`📦 Quantity spec edited: ${entry._original_quantity} → ${parsedQuantity}`)
      }
      
      return updatedEntry
    }))
  }, [])

  // Check if a spec is being edited
  const isSpecEditing = useCallback((entryId: string, specKey: string) => {
    return editingSpecs[entryId]?.[specKey] || false
  }, [editingSpecs])

  // Check if entry is complete
  const isEntryComplete = (entry: SmartEquipmentEntry) => {
    // Existing equipment is always complete (just needs inspection status)
    if (entry.existing_equipment_id) {
      return true
    }
    
    // For custom categories, check manual inputs
    if (entry.isCustomCategory) {
      return entry.name.trim() && entry.serial_number.trim() && entry.category_id && entry.customManufacturerName?.trim()
    }
    
    // For shackle categories, require shackle type selection
    if (isShackleCategory(entry.category_id) && !entry.shackleType) {
      return false
    }
    
    // For new equipment with batch IDs (offshore/h-sjakkel), check batch fields instead of serial number
    const hasSerial = needsBatchIds(entry.category_id, entry)
      ? (entry.batch_id_bow?.trim() && entry.batch_id_bolt?.trim())
      : entry.serial_number.trim()
    return entry.name.trim() && hasSerial && entry.category_id && entry.manufacturer_id
  }

  // Count valid entries
  const validEntriesCount = entries.filter(e => {
    // For custom categories, check manual inputs
    if (e.isCustomCategory) {
      return e.name.trim() && e.serial_number.trim() && e.category_id && e.customManufacturerName?.trim()
    }
    
    // For shackle categories, require shackle type
    const hasShackleType = !isShackleCategory(e.category_id) || e.shackleType
    
    const hasSerial = needsBatchIds(e.category_id, e)
      ? (e.batch_id_bow?.trim() || e.batch_id_bolt?.trim())
      : e.serial_number.trim()
    // For sling categories, require parts selection
    const hasSlingParts = !isSlingCategory(e.category_id) || e.slingParts
    // Grade is required for chain slings (R1), D-Sjakkel type, and H-Sjakkel type
    const needsGrade = isChainSlingCategory(e.category_id) || isDShackleType(e) || isHSjakkelType(e)
    const hasGrade = !needsGrade || e.grade
    return (e.name.trim() || hasSerial) && hasSlingParts && hasGrade && hasShackleType
  }).length

  // Submit all entries
  const handleSubmit = async () => {
    if (!companyId || !currentUser) return
    
    // Prepare entries with combined serial numbers and default location
    const preparedEntries = entries
      .filter(e => {
        // For custom categories, check manual inputs
        if (e.isCustomCategory) {
          return e.name.trim() && e.serial_number.trim() && e.category_id && e.customManufacturerName?.trim()
        }
        
        // For shackle categories, require shackle type
        const hasShackleType = !isShackleCategory(e.category_id) || e.shackleType
        
        const hasSerial = needsBatchIds(e.category_id, e)
          ? (e.batch_id_bow?.trim() || e.batch_id_bolt?.trim())
          : e.serial_number.trim()
        // For sling categories, require parts selection
        const hasSlingParts = !isSlingCategory(e.category_id) || e.slingParts
        // Grade is required for chain slings (R1), D-Sjakkel type, and H-Sjakkel type
        const needsGrade = isChainSlingCategory(e.category_id) || isDShackleType(e) || isHSjakkelType(e)
        const hasGrade = !needsGrade || e.grade
        return (e.name.trim() || hasSerial) && hasSlingParts && hasGrade && hasShackleType
      })
      .map(e => {
        // Check if this is existing equipment with changed quantity (inventory split)
        const quantityChanged = e.existing_equipment_id && 
          e._original_quantity !== undefined && 
          e.quantity !== undefined &&
          e.quantity !== e._original_quantity
        
        // Debug logging for inventory split detection
        if (e.existing_equipment_id) {
          console.log(`🔍 Inventory split check for ${e.name}:`, {
            existing_equipment_id: e.existing_equipment_id,
            original_quantity: e._original_quantity,
            current_quantity: e.quantity,
            quantity_changed: quantityChanged
          })
        }
        
        if (quantityChanged) {
          // Quantity changed - create NEW equipment instead of updating existing
          // Clear existing_equipment_id to trigger new equipment creation
          console.log(`Inventory split detected: ${e.name} quantity changed from ${e._original_quantity} to ${e.quantity}`)
          
          // Try to find a matching product_type_id based on equipment name
          // This is important for linking default documents (user manuals)
          let matchedProductTypeId = e.product_type_id
          if (!matchedProductTypeId && e._original_manufacturer_id && e._original_category_id) {
            // Look for product type that matches the equipment name pattern
            const key = `${e._original_category_id}-${e._original_manufacturer_id}`
            const productTypes = allProductTypes.get(key) || []
            const matchedType = productTypes.find(pt => 
              e.name.toLowerCase().includes(pt.type_name.toLowerCase()) ||
              pt.type_name.toLowerCase().includes(e.name.toLowerCase().split(' ')[0])
            )
            if (matchedType) {
              matchedProductTypeId = matchedType.id
              console.log(`📋 Matched product type for ${e.name}: ${matchedType.type_name} (${matchedType.id})`)
            }
          }
          
          // Log all data being sent for inventory split
          console.log(`📦 Inventory split data for ${e.name}:`, {
            category_id: e._original_category_id,
            manufacturer_id: e._original_manufacturer_id,
            product_type_id: matchedProductTypeId,
            wll: e.wll,
            batch_id_bow: e.batch_id_bow,
            batch_id_bolt: e.batch_id_bolt,
            standard: e.standard,
            grade: e.grade,
            quantity: e.quantity
          })
          
          return {
            ...e,
            existing_equipment_id: undefined, // Clear to create new
            // Restore category/manufacturer IDs for new equipment creation
            category_id: e._original_category_id || '',
            manufacturer_id: e._original_manufacturer_id || '',
            product_type_id: matchedProductTypeId || '',
            serial_number: buildSerialNumber(e),
            location: e.location || defaultLocation || '',
            // Don't include edited_specs - this will be new equipment
            edited_specs: undefined
          }
        }
        
        return {
          ...e,
          serial_number: buildSerialNumber(e),
          location: e.location || defaultLocation || '', // Apply default location if not set
          // For existing equipment, include edited specs for database update
          edited_specs: e.existing_equipment_id && e._existing_all_specs 
            ? e._existing_all_specs.map(spec => ({
                spec_key: spec.spec_key,
                value: spec.value
              }))
            : undefined
        }
      })
    
    if (preparedEntries.length === 0) {
      alert('Legg til minst ett utstyr')
      return
    }
    
    setIsSubmitting(true)
    setProgress({ current: 0, total: preparedEntries.length, message: 'Starter...' })
    
    try {
      // Determine who performed the inspection and who is responsible
      let performedById = currentUser.id
      let performedByName = currentUser.name
      let performedBySignature = currentUser.signature
      
      // Andreas Nilsen is always the responsible person (ordreansvarlig)
      // Try to find his signature from supervisors list, otherwise just use name
      const andreasNilsen = supervisors.find(s => s.name.toLowerCase().includes('andreas nilsen'))
      const responsiblePersonName = andreasNilsen?.name || 'Andreas Nilsen'
      const responsiblePersonSignature = andreasNilsen?.signature
      
      if (currentUser.isAdmin) {
        // Admin selected an inspector → inspector performed, Andreas Nilsen is responsible
        const selectedInspector = inspectors.find(i => i.id === selectedInspectorId)
        if (selectedInspector) {
          performedById = selectedInspector.id
          performedByName = selectedInspector.name
          performedBySignature = selectedInspector.signature
        }
      }
      // For inspectors: they performed, Andreas Nilsen is responsible (no selection needed)
      
      const bulkResult = await BulkEquipmentService.createBulkEquipment(
        preparedEntries,
        {
          companyId,
          generateControlCards: true,
          inspectorId: performedById,
          inspectorName: performedByName,
          inspectorSignature: performedBySignature,
          responsiblePersonName,
          responsiblePersonSignature,
          orderNo: orderNumber,
          serviceOrder: serviceOrder || undefined, // TechnipFMC: if set, uses so_no instead of order_no
          location: flowLocation
        },
        (current, total, message) => {
          setProgress({ current, total, message })
        }
      )
      
      setResult({
        success: bulkResult.failed.length === 0,
        created: bulkResult.created.length,
        failed: bulkResult.failed.length,
        blob: bulkResult.controlCardBlob
      })
      
      setEntries(prev => prev.map(entry => {
        const createdItem = bulkResult.created.find(c => c.entry_id === entry.id)
        const failedItem = bulkResult.failed.find(f => f.entry_id === entry.id)
        
        if (createdItem) {
          return { ...entry, status: 'saved' as const, equipment_id: createdItem.equipment_id, asset_number: createdItem.asset_number }
        }
        if (failedItem) {
          return { ...entry, status: 'error' as const, errors: [failedItem.error] }
        }
        return entry
      }))
    } catch {
      setResult({ success: false, created: 0, failed: entries.length })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Download control cards - tablet-friendly implementation
  const handleDownload = () => {
    if (!result?.blob) return
    
    const fileName = `kontrollkort-${companyName}-${new Date().toISOString().split('T')[0]}.pdf`
    const url = URL.createObjectURL(result.blob)
    
    // Check if we're on iOS (Safari has issues with programmatic downloads)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    
    if (isIOS || isSafari) {
      // For iOS/Safari: Open PDF in new tab (user can then share/save)
      const newWindow = window.open(url, '_blank')
      if (!newWindow) {
        // Popup blocked - try direct navigation
        window.location.href = url
      }
      // Don't revoke URL immediately on iOS - give time for the new window to load
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } else {
      // For other browsers: Use standard download approach
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 100)
    }
  }

  // Get display name for entry summary
  const getEntrySummary = (entry: SmartEquipmentEntry) => {
    // For existing equipment, use stored display names
    if (entry.existing_equipment_id) {
      const parts = []
      if (entry.name) parts.push(entry.name)
      if (entry._existing_manufacturer_name) parts.push(entry._existing_manufacturer_name)
      if (entry._existing_category_name) parts.push(entry._existing_category_name)
      return parts.length > 0 ? parts.join(' • ') : 'Eksisterende utstyr'
    }
    
    // For custom categories, use manual inputs
    if (entry.isCustomCategory) {
      const cat = getCategory(entry.category_id)
      const parts = []
      if (entry.name) parts.push(entry.name)
      if (entry.customManufacturerName) parts.push(entry.customManufacturerName)
      if (cat) parts.push(CATEGORY_DISPLAY_NAMES[cat.code] || cat.name_no || cat.name_en)
      return parts.length > 0 ? parts.join(' • ') : 'Tilpasset utstyr...'
    }
    
    // For new equipment, use samleliste category/manufacturer
    const cat = getCategory(entry.category_id)
    const mfrs = getManufacturersForCategory(entry.category_id)
    const mfr = mfrs.find(m => m.id === entry.manufacturer_id)
    
    const parts = []
    if (entry.name) parts.push(entry.name)
    if (mfr) parts.push(mfr.short_name || mfr.name)
    if (cat) parts.push(cat.name_en)
    
    return parts.length > 0 ? parts.join(' • ') : 'Nytt utstyr...'
  }

  // Check if entry is for existing equipment
  const isExistingEquipment = (entry: SmartEquipmentEntry) => !!entry.existing_equipment_id

  // Get serial display for summary
  const getSerialSummary = (entry: SmartEquipmentEntry) => {
    // For categories with batch IDs, show contracted serial number
    if (needsBatchIds(entry.category_id)) {
      if (entry.batch_id_bow || entry.batch_id_bolt) {
        return `${entry.batch_id_bow || '?'}-${entry.batch_id_bolt || '?'}`
      }
      return 'SN mangler'
    }
    return entry.serial_number || 'SN mangler'
  }

  usePageHeader('Periodisk kontroll', {
    subtitle: companyName || undefined,
    onBack: () => navigate(-1),
  })

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-[#1E3A5F] mx-auto mb-4" />
          <span className="text-2xl text-stone-700">Laster...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="shrink-0 bg-white border-b border-stone-200 px-4 py-2 flex items-center justify-end gap-4">
        <div className="text-right">
          {orderNumber && (
            <p className="text-sm font-mono font-semibold text-[#1E3A5F]">{orderNumber}</p>
          )}
          {serviceOrder && (
            <p className="text-xs font-mono text-amber-600">SO: {serviceOrder}</p>
          )}
          <span className="text-sm font-medium text-stone-600">
            {validEntriesCount} utstyr
          </span>
        </div>
      </div>

      {/* Progress bar */}
      {isSubmitting && (
        <div className="bg-blue-50 border-b-2 border-blue-200 px-4 py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-blue-200 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
            <span className="text-base font-medium text-blue-700 whitespace-nowrap">
              {progress.current}/{progress.total}
            </span>
          </div>
          <p className="text-sm text-blue-600 mt-2">{progress.message}</p>
        </div>
      )}

      {/* Result summary */}
      {result && (
        <div className={`border-b-2 px-4 py-4 ${result.success ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {result.success ? <CheckCircle className="text-emerald-600" size={28} /> : <AlertCircle className="text-amber-600" size={28} />}
              <span className={`text-lg font-medium ${result.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                {result.created} opprettet{result.failed > 0 && `, ${result.failed} feilet`}
              </span>
            </div>
            
            {result.blob && (
              <button
                onClick={handleDownload}
                className={`flex items-center gap-2 px-5 py-3 ${TOUCH.buttonHeight} bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 active:scale-95 transition-all`}
              >
                <Download size={22} />
                <span className="font-medium">Last ned PDF</span>
              </button>
            )}
          </div>
          
          {/* Action buttons after successful generation */}
          {result.success && (
            <div className="flex gap-3 pt-2 border-t border-emerald-200">
              <button
                onClick={() => {
                  // Neste: Clear everything and start fresh
                  setEntries([createEmptyEntry()])
                  setResult(null)
                  // Clear search-related states
                  setEquipmentSearchQuery({})
                  setEquipmentSearchResults({})
                  setIsSearching({})
                  setEditingSpecs({})
                }}
                className={`flex-1 flex items-center justify-center gap-2 ${TOUCH.buttonHeight} px-4 py-3 bg-[#1E3A5F] text-white font-medium rounded-xl hover:bg-[#2a4a73] active:scale-[0.98] transition-all`}
              >
                <Plus size={20} />
                <span>Neste</span>
              </button>
              <button
                onClick={() => {
                  // Kopier: Copy last entry's data (except serial number) to a new entry
                  const lastSavedEntry = entries.find(e => e.status === 'saved')
                  if (lastSavedEntry) {
                    const newEntry: SmartEquipmentEntry = {
                      ...lastSavedEntry,
                      id: generateTempId(),
                      serial_number: '',
                      batch_id_bow: '',
                      batch_id_bolt: '',
                      status: 'pending',
                      errors: [],
                      equipment_id: undefined,
                      asset_number: undefined,
                      existing_equipment_id: undefined
                    }
                    setEntries([newEntry])
                    setResult(null)
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-2 ${TOUCH.buttonHeight} px-4 py-3 bg-white text-[#1E3A5F] font-medium border-2 border-[#1E3A5F] rounded-xl hover:bg-stone-50 active:scale-[0.98] transition-all`}
              >
                <Copy size={20} />
                <span>Kopier</span>
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className={`flex-1 flex items-center justify-center gap-2 ${TOUCH.buttonHeight} px-4 py-3 bg-white text-stone-600 font-medium border-2 border-stone-300 rounded-xl hover:bg-stone-50 active:scale-[0.98] transition-all`}
              >
                <X size={20} />
                <span>Avbryt</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Equipment entries */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {entries.map((entry) => {
          const manufacturers = getManufacturersForCategory(entry.category_id)
          
          return (
            <div
              key={entry.id}
              className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${
                entry.status === 'saved' ? 'border-emerald-400 bg-emerald-50' :
                entry.status === 'error' ? 'border-red-400 bg-red-50' :
                'border-[#1E3A5F] shadow-lg'
              }`}
            >
              {/* Error message */}
              {entry.status === 'error' && entry.errors.length > 0 && (
                <div className="px-4 py-3 bg-red-50 border-b border-red-200">
                  <p className="text-sm text-red-600 font-medium">{entry.errors.join(', ')}</p>
                </div>
              )}
              
              {/* Form - always visible */}
              {entry.status !== 'saved' && (
                <div className="border-t-2 border-stone-100 p-3 space-y-3">
                  
                  {/* EXISTING EQUIPMENT - Show read-only details */}
                  {isExistingEquipment(entry) ? (
                    <>
                      {/* Existing equipment details - read only, show all specs like control card */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-amber-700">
                            Eksisterende utstyr - oppdater kontrollkort
                          </p>
                          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                            {entry._existing_category_name || 'Utstyr'}
                          </span>
                        </div>
                        
                        {/* Equipment name header */}
                        <div className="mb-2 pb-2 border-b border-amber-200">
                          <span className="font-semibold text-stone-800">{entry.name || 'Ukjent utstyr'}</span>
                          {entry.serial_number && (
                            <span className="ml-2 text-sm text-stone-500">SN: {entry.serial_number}</span>
                          )}
                        </div>
                        
                        {/* All specs in 2-column grid (like control card) - with edit option */}
                        {entry._existing_all_specs && entry._existing_all_specs.length > 0 ? (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            {entry._existing_all_specs.map((spec, idx) => (
                              <div key={idx} className="flex flex-col">
                                <span className="text-xs text-stone-500">{spec.label}</span>
                                {isSpecEditing(entry.id, spec.spec_key) ? (
                                  <div className="flex items-center gap-1">
                                    {/* Date input for NDT date fields */}
                                    {(spec.spec_key === 'last_ndt_date' || spec.spec_key === 'ndt_date') ? (
                                      <input
                                        type="date"
                                        value={(() => {
                                          // Convert dd.mm.yyyy to yyyy-mm-dd for date input
                                          if (spec.value && spec.value !== '-') {
                                            const parts = spec.value.split('.')
                                            if (parts.length === 3) {
                                              return `${parts[2]}-${parts[1]}-${parts[0]}`
                                            }
                                          }
                                          return ''
                                        })()}
                                        onChange={(e) => {
                                          // Convert yyyy-mm-dd to dd.mm.yyyy for display
                                          if (e.target.value) {
                                            const date = new Date(e.target.value)
                                            const formatted = date.toLocaleDateString('nb-NO', {
                                              year: 'numeric',
                                              month: '2-digit',
                                              day: '2-digit'
                                            })
                                            updateExistingSpec(entry.id, spec.spec_key, formatted)
                                          } else {
                                            updateExistingSpec(entry.id, spec.spec_key, '-')
                                          }
                                        }}
                                        className="flex-1 px-2 py-1 text-sm font-medium text-stone-800 border border-amber-400 rounded focus:border-amber-500 focus:outline-none bg-white"
                                        autoFocus
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={spec.value}
                                        onChange={(e) => updateExistingSpec(entry.id, spec.spec_key, e.target.value)}
                                        className="flex-1 px-2 py-1 text-sm font-medium text-stone-800 border border-amber-400 rounded focus:border-amber-500 focus:outline-none bg-white"
                                        autoFocus
                                      />
                                    )}
                                    {spec.unit_symbol && (
                                      <span className="text-xs text-stone-500 whitespace-nowrap">{spec.unit_symbol}</span>
                                    )}
                                    <button
                                      onClick={() => toggleSpecEdit(entry.id, spec.spec_key)}
                                      className="p-1 text-emerald-600 hover:text-emerald-700"
                                      title="Lagre"
                                    >
                                      <Check size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium text-stone-800 flex-1">
                                      {spec.value}{spec.unit_symbol ? ` ${spec.unit_symbol}` : ''}
                                    </span>
                                    <button
                                      onClick={() => toggleSpecEdit(entry.id, spec.spec_key)}
                                      className="p-1 text-amber-600 hover:text-amber-700 hover:bg-amber-100 rounded"
                                      title="Rediger"
                                    >
                                      <Pencil size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-stone-500 italic">Ingen spesifikasjoner funnet</p>
                        )}
                        
                        {/* Button to clear and switch to new equipment */}
                        <button
                          onClick={() => {
                            setEntries(prev => prev.map(e => 
                              e.id === entry.id 
                                ? createEmptyEntry() 
                                : e
                            ).map((e, i) => i === entries.findIndex(x => x.id === entry.id) ? { ...e, id: entry.id } : e))
                          }}
                          className="mt-3 text-xs text-amber-700 hover:text-amber-900 underline"
                        >
                          Bytt til nytt utstyr
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* NEW EQUIPMENT - Show search and dropdowns */}
                      {/* Equipment Search - find existing equipment */}
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">
                          Søk eksisterende utstyr
                        </label>
                        <div className="relative">
                          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                          <input
                            type="text"
                            value={equipmentSearchQuery[entry.id] || ''}
                            onChange={(e) => handleEquipmentSearch(entry.id, e.target.value)}
                            placeholder="Søk på navn, SN eller ID..."
                            className={`w-full pl-10 pr-10 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                          {equipmentSearchQuery[entry.id] && (
                            <button
                              onClick={() => clearEquipmentSearch(entry.id)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                            >
                              <X size={18} />
                            </button>
                          )}
                        </div>
                        
                        {/* Search results dropdown */}
                        {(equipmentSearchResults[entry.id]?.length > 0 || isSearching[entry.id]) && (
                          <div className="mt-2 bg-white border-2 border-stone-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                            {isSearching[entry.id] ? (
                              <div className="p-3 flex items-center justify-center gap-2 text-stone-500">
                                <Loader2 size={16} className="animate-spin" />
                                <span className="text-sm">Søker...</span>
                              </div>
                            ) : (
                              equipmentSearchResults[entry.id]?.map((eq) => (
                                <button
                                  key={eq.id}
                                  onClick={() => selectExistingEquipment(entry.id, eq)}
                                  className="w-full text-left px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-stone-800">{eq.name}</span>
                                    <div className="flex items-center gap-1.5">
                                      {/* Show Antall for R4 categories (shackles, etc.) - not for slings R1/R2/R3 */}
                                      {eq.quantity !== null && !['R1', 'R2', 'R3'].some(code => eq.category_code?.startsWith(code)) && (
                                        <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                                          Antall: {eq.quantity}
                                        </span>
                                      )}
                                      {eq.wll && (
                                        <span className="text-xs font-semibold text-[#1E3A5F] bg-blue-100 px-2 py-0.5 rounded">
                                          {eq.wll}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-xs text-stone-500 flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                    <span>{eq.category_name}</span>
                                    {eq.manufacturer_name && <span>• {eq.manufacturer_name}</span>}
                                    {eq.serial_number && <span>• SN: {eq.serial_number}</span>}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="border-t border-stone-200 pt-3">
                        <p className="text-xs text-stone-400 mb-2">Eller velg manuelt:</p>
                      </div>
                      
                      {/* Equipment Type - also sets the equipment name */}
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Utstyrstype *</label>
                        <select
                          value={entry.isCustomCategory ? 'TILPASSET' : entry.category_id}
                          onChange={(e) => {
                            if (e.target.value === 'TILPASSET') {
                              // User selected "Tilpasset" - show all categories dropdown
                              updateEntry(entry.id, { 
                                isCustomCategory: true,
                                category_id: '', // Reset category until they pick from all list
                                name: '',
                                slingParts: undefined
                              })
                            } else {
                              const selectedCat = categories.find(c => c.id === e.target.value)
                              const displayName = selectedCat ? (CATEGORY_DISPLAY_NAMES[selectedCat.code] || selectedCat.name_en) : ''
                              updateEntry(entry.id, { 
                                isCustomCategory: false,
                                category_id: e.target.value,
                                name: displayName, // Set equipment name from category
                                slingParts: undefined // Reset sling parts when category changes
                              })
                            }
                          }}
                          className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                        >
                          <option value="">Velg utstyrstype...</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>
                              {CATEGORY_DISPLAY_NAMES[c.code] || c.name_en}
                            </option>
                          ))}
                          <option value="TILPASSET" className="font-semibold">⚙️ Tilpasset...</option>
                        </select>
                      </div>
                      
                      {/* Custom Category Selection - shown when "Tilpasset" is selected */}
                      {entry.isCustomCategory && (
                        <div className="relative">
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Søk kategori *</label>
                          <input
                            type="text"
                            value={entry.category_id 
                              ? getCategoryDisplayName(allAvailableCategories.find(c => c.id === entry.category_id) || { code: '', name_no: '', name_en: '' } as SamlelisteCategory)
                              : (customCategorySearch[entry.id] || '')
                            }
                            onChange={(e) => {
                              setCustomCategorySearch(prev => ({ ...prev, [entry.id]: e.target.value }))
                              setShowCategoryDropdown(prev => ({ ...prev, [entry.id]: true }))
                              // Clear selection if typing
                              if (entry.category_id) {
                                updateEntry(entry.id, { 
                                  category_id: '',
                                  name: '',
                                  customSpecValues: {}
                                })
                              }
                            }}
                            onFocus={() => setShowCategoryDropdown(prev => ({ ...prev, [entry.id]: true }))}
                            placeholder="Søk etter kategori..."
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                          />
                          {/* Clear button */}
                          {entry.category_id && (
                            <button
                              type="button"
                              onClick={() => {
                                setCustomCategorySearch(prev => ({ ...prev, [entry.id]: '' }))
                                updateEntry(entry.id, { 
                                  category_id: '',
                                  name: '',
                                  customSpecValues: {}
                                })
                              }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 mt-3 text-stone-400 hover:text-stone-600"
                            >
                              ✕
                            </button>
                          )}
                          {/* Dropdown results */}
                          {showCategoryDropdown[entry.id] && !entry.category_id && (
                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-amber-400 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                              {getFilteredCustomCategories(customCategorySearch[entry.id] || '').length === 0 ? (
                                <div className="px-4 py-3 text-sm text-stone-500">Ingen kategorier funnet</div>
                              ) : (
                                getFilteredCustomCategories(customCategorySearch[entry.id] || '').slice(0, 15).map(c => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => {
                                      const displayName = getCategoryDisplayName(c)
                                      setCustomCategorySearch(prev => ({ ...prev, [entry.id]: '' }))
                                      setShowCategoryDropdown(prev => ({ ...prev, [entry.id]: false }))
                                      
                                      // Update entry with selected category
                                      updateEntry(entry.id, { 
                                        category_id: c.id,
                                        name: displayName,
                                        slingParts: undefined,
                                        customSpecValues: {}
                                      })
                                      
                                      // Load spec templates
                                      loadCustomSpecTemplates(c.id).then(templates => {
                                        const initialSpecValues: Record<string, string> = {}
                                        for (const t of templates) {
                                          initialSpecValues[t.spec_key] = t.default_value || ''
                                        }
                                        updateEntry(entry.id, { customSpecValues: initialSpecValues })
                                      })
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-amber-50 border-b border-amber-100 last:border-b-0 transition-colors"
                                  >
                                    <span className="font-medium">{getCategoryDisplayName(c)}</span>
                                  </button>
                                ))
                              )}
                              {getFilteredCustomCategories(customCategorySearch[entry.id] || '').length > 15 && (
                                <div className="px-4 py-2 text-xs text-stone-400 bg-stone-50">
                                  +{getFilteredCustomCategories(customCategorySearch[entry.id] || '').length - 15} flere - skriv for å filtrere
                                </div>
                              )}
                            </div>
                          )}
                          {/* Click outside to close */}
                          {showCategoryDropdown[entry.id] && !entry.category_id && (
                            <div 
                              className="fixed inset-0 z-40" 
                              onClick={() => setShowCategoryDropdown(prev => ({ ...prev, [entry.id]: false }))}
                            />
                          )}
                        </div>
                      )}
                      
                      {/* Sling Parts and Grade Selection - only for Chain Sling (R1) and Wire Sling (R3), not for custom */}
                      {entry.category_id && isSlingCategory(entry.category_id) && !entry.isCustomCategory && (() => {
                        const cat = getCategory(entry.category_id)
                        const isChainSling = cat?.code === 'R1'
                        return (
                          <div className="flex flex-col sm:flex-row gap-4">
                            {/* Parts Selection */}
                            <div className="flex-1">
                              <label className="block text-xs font-semibold text-stone-500 mb-2">Antall parter *</label>
                              <div className="flex flex-wrap gap-2">
                                {[1, 2, 3, 4, 5].map(parts => (
                                  <button
                                    key={parts}
                                    type="button"
                                    onClick={() => {
                                      const partName = parts === 1 ? '1-Part' : `${parts}-Part`
                                      const baseName = isChainSling ? 'Chain Sling' : 'Wire Sling'
                                      const gradeStr = isChainSling && entry.grade ? ` Grade ${entry.grade}` : ''
                                      updateEntry(entry.id, { 
                                        slingParts: parts,
                                        name: `${partName} ${baseName}${gradeStr}` // Update name with parts and grade
                                      })
                                    }}
                                    className={`px-4 py-2 text-sm font-semibold rounded-xl border-2 transition-all ${
                                      entry.slingParts === parts
                                        ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]'
                                        : 'bg-white text-stone-600 border-stone-300 hover:border-[#1E3A5F]'
                                    }`}
                                  >
                                    {parts}-Part
                                  </button>
                                ))}
                              </div>
                              {!entry.slingParts && (
                                <p className="text-xs text-amber-600 mt-1">Velg antall parter for å fortsette</p>
                              )}
                            </div>
                            
                            {/* Grade Selection - only for Chain Sling (R1) after parts are selected */}
                            {isChainSling && entry.slingParts && (
                              <div>
                                <label className="block text-xs font-semibold text-stone-500 mb-2">Grade *</label>
                                <div className="flex gap-2">
                                  {['80', '100'].map(grade => (
                                    <button
                                      key={grade}
                                      type="button"
                                      onClick={() => {
                                        const partName = entry.slingParts === 1 ? '1-Part' : `${entry.slingParts}-Part`
                                        updateEntry(entry.id, { 
                                          grade,
                                          name: `${partName} Chain Sling Grade ${grade}` // Update name with grade
                                        })
                                      }}
                                      className={`px-5 py-2 text-sm font-bold rounded-xl border-2 transition-all ${
                                        entry.grade === grade
                                          ? 'bg-amber-500 text-white border-amber-500'
                                          : 'bg-white text-stone-600 border-stone-300 hover:border-amber-500'
                                      }`}
                                    >
                                      {grade}
                                    </button>
                                  ))}
                                </div>
                                {!entry.grade && (
                                  <p className="text-xs text-amber-600 mt-1">Velg grade</p>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })()}
                      
                      {/* Shackle Type Selection - for Sjakkel (R4-2) category */}
                      {entry.category_id && isShackleCategory(entry.category_id) && !entry.isCustomCategory && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-2">Sjakkeltype *</label>
                          <div className="flex flex-wrap gap-2">
                            {SHACKLE_TYPES.map(st => (
                              <button
                                key={st.value}
                                type="button"
                                onClick={() => {
                                  updateEntry(entry.id, { 
                                    shackleType: st.value,
                                    name: st.label,
                                    // Reset fields when changing shackle type
                                    manufacturer_id: '',
                                    product_type_id: '',
                                    wll: '',
                                    dimension: '',
                                    grade: '',
                                    batch_id_bow: '',
                                    batch_id_bolt: ''
                                  })
                                }}
                                className={`px-4 py-2 text-sm font-semibold rounded-xl border-2 transition-all ${
                                  entry.shackleType === st.value
                                    ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]'
                                    : 'bg-white text-stone-600 border-stone-300 hover:border-[#1E3A5F]'
                                }`}
                              >
                                {st.label}
                              </button>
                            ))}
                          </div>
                          {!entry.shackleType && (
                            <p className="text-xs text-amber-600 mt-1">Velg sjakkeltype for å fortsette</p>
                          )}
                        </div>
                      )}
                      
                      {/* Manufacturer (only show when category is selected and shackle type is selected for shackles) */}
                      {entry.category_id && !entry.isCustomCategory && (!isShackleCategory(entry.category_id) || entry.shackleType) && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Produsent *</label>
                          <select
                            value={entry.manufacturer_id}
                            onChange={(e) => {
                              const mfrId = e.target.value
                              updateEntry(entry.id, { 
                                manufacturer_id: mfrId,
                                product_type_id: '', // Reset product type when manufacturer changes
                                wll: '',
                                dimension: ''
                              })
                              // Load product types for shackles, D-sjakkel, or eye bolts (RUD) when manufacturer is selected
                              if ((isShackleCategory(entry.category_id) || isDShackleCategory(entry.category_id) || isEyeBoltCategory(entry.category_id)) && mfrId) {
                                loadProductTypes(entry.category_id, mfrId)
                              }
                            }}
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          >
                            <option value="">Velg produsent...</option>
                            {manufacturers.map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* Manual inputs for Tilpasset (Custom) categories */}
                      {entry.category_id && entry.isCustomCategory && (
                        <>
                          {/* Produsent - manual text input */}
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">Produsent *</label>
                            <input
                              type="text"
                              value={entry.customManufacturerName || ''}
                              onChange={(e) => updateEntry(entry.id, { customManufacturerName: e.target.value })}
                              placeholder="Skriv inn produsent..."
                              className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                            />
                          </div>
                          
                          {/* Serienummer - manual text input */}
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">Serienummer *</label>
                            <input
                              type="text"
                              value={entry.serial_number}
                              onChange={(e) => updateEntry(entry.id, { serial_number: e.target.value })}
                              placeholder="Serienummer"
                              className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                            />
                          </div>
                          
                          {/* Dynamic Spec Templates for Custom Category */}
                          {getCustomSpecTemplates(entry.category_id).length > 0 && (
                            <div className="col-span-full mt-2">
                              <div className="border-t border-amber-300 pt-3">
                                <p className="text-xs font-semibold text-amber-700 mb-2">📋 Spesifikasjoner for denne kategorien</p>
                                <div className="grid grid-cols-2 gap-3">
                                  {getCustomSpecTemplates(entry.category_id)
                                    .filter(spec => !['manufacturer'].includes(spec.spec_key)) // Skip manufacturer (already handled above)
                                    .map(spec => (
                                      <div key={spec.id}>
                                        <label className="block text-xs font-semibold text-stone-500 mb-1">
                                          {spec.label_no || spec.label_en || spec.spec_key}
                                          {spec.unit_symbol && <span className="text-stone-400 ml-1">({spec.unit_symbol})</span>}
                                          {spec.is_required && <span className="text-red-500 ml-0.5">*</span>}
                                        </label>
                                        {spec.data_type === 'boolean' ? (
                                          <select
                                            value={entry.customSpecValues?.[spec.spec_key] || ''}
                                            onChange={(e) => {
                                              updateEntry(entry.id, { 
                                                customSpecValues: { 
                                                  ...(entry.customSpecValues || {}), 
                                                  [spec.spec_key]: e.target.value 
                                                } 
                                              })
                                            }}
                                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                                          >
                                            <option value="">Velg...</option>
                                            <option value="true">Ja</option>
                                            <option value="false">Nei</option>
                                          </select>
                                        ) : spec.data_type === 'number' || spec.data_type === 'decimal' ? (
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={entry.customSpecValues?.[spec.spec_key] || ''}
                                            onChange={(e) => {
                                              updateEntry(entry.id, { 
                                                customSpecValues: { 
                                                  ...(entry.customSpecValues || {}), 
                                                  [spec.spec_key]: e.target.value 
                                                } 
                                              })
                                            }}
                                            placeholder={spec.label_no || spec.label_en || ''}
                                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                                          />
                                        ) : spec.data_type === 'date' ? (
                                          <input
                                            type="date"
                                            value={entry.customSpecValues?.[spec.spec_key] || ''}
                                            onChange={(e) => {
                                              updateEntry(entry.id, { 
                                                customSpecValues: { 
                                                  ...(entry.customSpecValues || {}), 
                                                  [spec.spec_key]: e.target.value 
                                                } 
                                              })
                                            }}
                                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                                          />
                                        ) : (
                                          <input
                                            type="text"
                                            value={entry.customSpecValues?.[spec.spec_key] || ''}
                                            onChange={(e) => {
                                              updateEntry(entry.id, { 
                                                customSpecValues: { 
                                                  ...(entry.customSpecValues || {}), 
                                                  [spec.spec_key]: e.target.value 
                                                } 
                                              })
                                            }}
                                            placeholder={spec.label_no || spec.label_en || ''}
                                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-amber-400 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-amber-50`}
                                          />
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Modell (Model) - for Offshore Sjakkel after manufacturer is selected */}
                      {entry.category_id && entry.manufacturer_id && isOffshoreSjakkelType(entry) && getFilteredProductTypes(entry.category_id, entry.manufacturer_id, 'offshore').length > 0 && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Modell</label>
                          <select
                            value={entry.product_type_id || ''}
                            onChange={(e) => {
                              const productType = getProductType(entry.category_id, entry.manufacturer_id, e.target.value)
                              updateEntry(entry.id, { 
                                product_type_id: e.target.value,
                                name: productType?.type_name || entry.name,
                                standard: productType?.standard || '',
                                grade: productType?.grade || '',
                                wll: '', // Reset WLL when model changes
                                dimension: ''
                              })
                            }}
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          >
                            <option value="">Velg modell...</option>
                            {getFilteredProductTypes(entry.category_id, entry.manufacturer_id, 'offshore').map(pt => (
                              <option key={pt.id} value={pt.id}>{pt.type_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* Modell (Model) - for D-Sjakkel after manufacturer is selected */}
                      {entry.category_id && entry.manufacturer_id && isDShackleType(entry) && getFilteredProductTypes(entry.category_id, entry.manufacturer_id, 'd-sjakkel').length > 0 && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Modell</label>
                          <select
                            value={entry.product_type_id || ''}
                            onChange={(e) => {
                              const productType = getProductType(entry.category_id, entry.manufacturer_id, e.target.value)
                              updateEntry(entry.id, { 
                                product_type_id: e.target.value,
                                name: productType?.type_name || entry.name,
                                standard: productType?.standard || '',
                                grade: '', // Reset grade when model changes
                                wll: '',
                                dimension: ''
                              })
                            }}
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          >
                            <option value="">Velg modell...</option>
                            {getFilteredProductTypes(entry.category_id, entry.manufacturer_id, 'd-sjakkel').map(pt => (
                              <option key={pt.id} value={pt.id}>{pt.type_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* Grade Selection - for D-Sjakkel type after model is selected */}
                      {entry.category_id && entry.manufacturer_id && isDShackleType(entry) && entry.product_type_id && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-2">Grade *</label>
                          <div className="flex gap-2">
                            {['60', '80'].map(grade => (
                              <button
                                key={grade}
                                type="button"
                                onClick={() => {
                                  const productType = getProductType(entry.category_id, entry.manufacturer_id, entry.product_type_id!)
                                  updateEntry(entry.id, { 
                                    grade,
                                    name: `${productType?.type_name || 'D-Sjakkel'} Grade ${grade}`
                                  })
                                }}
                                className={`px-5 py-2 text-sm font-bold rounded-xl border-2 transition-all ${
                                  entry.grade === grade
                                    ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-white text-stone-600 border-stone-300 hover:border-amber-500'
                                }`}
                              >
                                {grade}
                              </button>
                            ))}
                          </div>
                          {!entry.grade && (
                            <p className="text-xs text-amber-600 mt-1">Velg grade</p>
                          )}
                        </div>
                      )}
                      
                      {/* Grade Selection - for H-Sjakkel type after manufacturer is selected */}
                      {entry.category_id && entry.manufacturer_id && isHSjakkelType(entry) && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-2">Grade *</label>
                          <div className="flex gap-2">
                            {['60', '80'].map(grade => (
                              <button
                                key={grade}
                                type="button"
                                onClick={() => {
                                  updateEntry(entry.id, { 
                                    grade,
                                    name: `H-Sjakkel Grade ${grade}`
                                  })
                                }}
                                className={`px-5 py-2 text-sm font-bold rounded-xl border-2 transition-all ${
                                  entry.grade === grade
                                    ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-white text-stone-600 border-stone-300 hover:border-amber-500'
                                }`}
                              >
                                {grade}
                              </button>
                            ))}
                          </div>
                          {!entry.grade && (
                            <p className="text-xs text-amber-600 mt-1">Velg grade</p>
                          )}
                        </div>
                      )}
                      
                      {/* Modell (Model) - only for Eye Bolt (R4-4) with RUD manufacturer */}
                      {entry.category_id && entry.manufacturer_id && isEyeBoltCategory(entry.category_id) && isRudManufacturer(entry.manufacturer_id) && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Modell</label>
                          <select
                            value={entry.product_type_id || ''}
                            onChange={(e) => {
                              const productType = getProductType(entry.category_id, entry.manufacturer_id, e.target.value)
                              updateEntry(entry.id, { 
                                product_type_id: e.target.value,
                                name: productType?.type_name || entry.name,
                                wll: '', // Reset WLL when model changes
                                dimension: ''
                              })
                            }}
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          >
                            <option value="">Velg modell...</option>
                            {getProductTypes(entry.category_id, entry.manufacturer_id).map(pt => (
                              <option key={pt.id} value={pt.id}>{pt.type_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* WLL input and Gjenge dropdown - for all Eye Bolts */}
                      {entry.category_id && entry.manufacturer_id && isEyeBoltCategory(entry.category_id) && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">WLL (t) *</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={entry.wll || ''}
                              onChange={(e) => {
                                // Allow numbers, comma, period for decimal input
                                const value = e.target.value.replace(/[^0-9.,]/g, '')
                                updateEntry(entry.id, { wll: value })
                              }}
                              placeholder="f.eks. 1,5"
                              className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">Gjenge</label>
                            <select
                              value={entry.dimension || ''}
                              onChange={(e) => updateEntry(entry.id, { dimension: e.target.value })}
                              className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                            >
                              <option value="">Velg gjenge...</option>
                              {getThreadOptions().map(thread => (
                                <option key={thread} value={thread}>{thread}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      
                      {/* WLL dropdown - for Offshore Sjakkel with model selected */}
                      {entry.category_id && entry.manufacturer_id && isOffshoreSjakkelType(entry) && entry.product_type_id && getWllOptions(entry.category_id, entry.manufacturer_id, entry.product_type_id).length > 0 && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">WLL *</label>
                            <select
                              value={entry.wll || ''}
                              onChange={(e) => {
                                const wll = e.target.value
                                const dimension = getDimensionForWll(entry.category_id, entry.manufacturer_id, entry.product_type_id!, wll)
                                updateEntry(entry.id, { 
                                  wll,
                                  dimension: dimension || ''
                                })
                              }}
                              className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                            >
                              <option value="">Velg WLL...</option>
                              {getWllOptions(entry.category_id, entry.manufacturer_id, entry.product_type_id).map(wll => (
                                <option key={wll} value={wll}>{wll}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">Dimensjon</label>
                            <input
                              type="text"
                              value={entry.dimension || ''}
                              readOnly
                              placeholder="Auto-fylles"
                              className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-200 rounded-xl bg-stone-50 text-stone-600`}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* WLL manual input - for D-Sjakkel type after model and grade are selected */}
                      {entry.category_id && entry.manufacturer_id && isDShackleType(entry) && entry.product_type_id && entry.grade && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">WLL *</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={entry.wll || ''}
                            onChange={(e) => {
                              // Replace comma with period and only allow numbers, periods, and 't'
                              let value = e.target.value
                                .replace(',', '.')
                                .replace(/[^0-9.t]/gi, '')
                              // Ensure only one period
                              const parts = value.split('.')
                              if (parts.length > 2) {
                                value = parts[0] + '.' + parts.slice(1).join('')
                              }
                              updateEntry(entry.id, { wll: value })
                            }}
                            placeholder="f.eks. 2.5t"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                      
                      {/* WLL manual input - for H-Sjakkel type after grade is selected */}
                      {entry.category_id && entry.manufacturer_id && isHSjakkelType(entry) && entry.grade && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">WLL *</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={entry.wll || ''}
                            onChange={(e) => {
                              // Replace comma with period and only allow numbers, periods, and 't'
                              let value = e.target.value
                                .replace(',', '.')
                                .replace(/[^0-9.t]/gi, '')
                              // Ensure only one period
                              const parts = value.split('.')
                              if (parts.length > 2) {
                                value = parts[0] + '.' + parts.slice(1).join('')
                              }
                              updateEntry(entry.id, { wll: value })
                            }}
                            placeholder="f.eks. 2.5t"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                  
                  {/* WLL or Kapasitet - shows after manufacturer is selected (NOT for Shackles, D-Sjakkel, or Eye Bolts - they have their own WLL input) */}
                  {entry.manufacturer_id && !isShackleCategory(entry.category_id) && !isDShackleCategory(entry.category_id) && !isEyeBoltCategory(entry.category_id) && (
                    <div className={`grid ${shouldShowQuantity(entry.category_id) ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                      {/* Kapasitet (kg) for FAL categories, WLL for others */}
                      {isFallProtectionCategory(entry.category_id) ? (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Kapasitet (kg)</label>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            value={entry.capacity || ''}
                            onChange={(e) => updateEntry(entry.id, { capacity: parseInt(e.target.value) || undefined })}
                            placeholder="f.eks. 140"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">WLL</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={entry.wll || ''}
                            onChange={(e) => {
                              // Replace comma with period and only allow numbers, periods, and 't'
                              let value = e.target.value
                                .replace(',', '.')
                                .replace(/[^0-9.t]/gi, '') // Only allow digits, period, and 't'
                              // Ensure only one period
                              const parts = value.split('.')
                              if (parts.length > 2) {
                                value = parts[0] + '.' + parts.slice(1).join('')
                              }
                              updateEntry(entry.id, { wll: value })
                            }}
                            placeholder="f.eks. 2.5t"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                      
                      {/* Antall Input - NOT shown for slings (R1, R2, R3) or custom categories */}
                      {shouldShowQuantity(entry.category_id) && !entry.isCustomCategory && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Antall *</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={entry.quantity || ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '')
                              updateEntry(entry.id, { quantity: val ? parseInt(val) : undefined })
                            }}
                            placeholder="Antall"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Length Input - for Fiber Sling (R2) categories */}
                  {entry.manufacturer_id && isFiberSlingCategory(entry.category_id) && (
                    <div>
                      <label className="block text-xs font-semibold text-stone-500 mb-1">Lengde (m)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={entry.length || ''}
                        onChange={(e) => {
                          // Allow numbers and decimal separator
                          const value = e.target.value.replace(/[^0-9.,]/g, '')
                          updateEntry(entry.id, { length: value })
                        }}
                        placeholder="f.eks. 3"
                        className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                      />
                    </div>
                  )}
                  
                  {/* Antall Input - for Offshore Sjakkel (shown after model and WLL) */}
                  {entry.manufacturer_id && isOffshoreSjakkelType(entry) && entry.product_type_id && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Antall *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={entry.quantity || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '')
                            updateEntry(entry.id, { quantity: val ? parseInt(val) : undefined })
                          }}
                          placeholder="Antall"
                          className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Antall Input - for D-Sjakkel type (shown after model, grade and WLL) */}
                  {entry.manufacturer_id && isDShackleType(entry) && entry.product_type_id && entry.grade && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Antall *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={entry.quantity || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '')
                            updateEntry(entry.id, { quantity: val ? parseInt(val) : undefined })
                          }}
                          placeholder="Antall"
                          className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Antall Input - for H-Sjakkel type (shown after grade and WLL) */}
                  {entry.manufacturer_id && isHSjakkelType(entry) && entry.grade && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Antall *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={entry.quantity || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '')
                            updateEntry(entry.id, { quantity: val ? parseInt(val) : undefined })
                          }}
                          placeholder="Antall"
                          className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Antall Input - for Eye Bolts (shown separately since WLL/Gjenge is above) */}
                  {entry.manufacturer_id && isEyeBoltCategory(entry.category_id) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Antall *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={entry.quantity || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '')
                            updateEntry(entry.id, { quantity: val ? parseInt(val) : undefined })
                          }}
                          placeholder="Antall"
                          className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Batch IDs - for offshore/h-shackles (not for D-Sjakkel or custom categories) */}
                  {entry.category_id && needsBatchIds(entry.category_id, entry) && !entry.isCustomCategory && (
                    <div className="grid grid-cols-2 gap-3">
                      {getCategory(entry.category_id)?.has_batch_bow && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Batch ID Bue</label>
                          <input
                            type="text"
                            value={entry.batch_id_bow || ''}
                            onChange={(e) => updateEntry(entry.id, { batch_id_bow: e.target.value })}
                            placeholder="Batch ID"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                      {getCategory(entry.category_id)?.has_batch_bolt && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Batch ID Bolt</label>
                          <input
                            type="text"
                            value={entry.batch_id_bolt || ''}
                            onChange={(e) => updateEntry(entry.id, { batch_id_bolt: e.target.value })}
                            placeholder="Batch ID"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Serial Number - only for categories WITHOUT batch IDs (not for custom categories) */}
                  {entry.category_id && !needsBatchIds(entry.category_id) && !entry.isCustomCategory && (
                    <div>
                      <label className="block text-xs font-semibold text-stone-500 mb-1">Serienummer *</label>
                      <input
                        type="text"
                        value={entry.serial_number}
                        onChange={(e) => updateEntry(entry.id, { serial_number: e.target.value })}
                        placeholder="Serienummer"
                        className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                      />
                    </div>
                  )}

                  {/* Hook checkbox and ID input - for Fiber Sling (R2) categories, after serial number */}
                  {entry.manufacturer_id && isFiberSlingCategory(entry.category_id) && (
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={entry.hasHook || false}
                          onChange={(e) => updateEntry(entry.id, { 
                            hasHook: e.target.checked,
                            hookId: e.target.checked ? entry.hookId : undefined 
                          })}
                          className="w-5 h-5 rounded border-2 border-stone-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                        />
                        <span className="text-sm font-medium text-stone-700">Krok</span>
                      </label>
                      {entry.hasHook && (
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Krok serienummer *</label>
                          <input
                            type="text"
                            value={entry.hookId || ''}
                            onChange={(e) => updateEntry(entry.id, { hookId: e.target.value.toUpperCase() })}
                            placeholder="Serienummer"
                            className={`w-full px-3 py-2 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none`}
                          />
                        </div>
                      )}
                    </div>
                  )}
                    </>
                  )}
                  
                  {/* Generate NDT Report checkbox - for R1 (chain slings) when performer has MT qualification */}
                  {entry.category_id && isChainSlingCategory(entry.category_id) && (
                    <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                      <input
                        type="checkbox"
                        id={`ndt-report-${entry.id}`}
                        checked={entry.generateNdtReport || false}
                        onChange={(e) => updateEntry(entry.id, { generateNdtReport: e.target.checked })}
                        disabled={!effectiveMtQualification}
                        className={`w-5 h-5 rounded border-2 ${effectiveMtQualification ? 'border-blue-400 text-blue-600' : 'border-stone-300 text-stone-400'} focus:ring-blue-500`}
                      />
                      <label 
                        htmlFor={`ndt-report-${entry.id}`} 
                        className={`text-sm font-medium cursor-pointer ${effectiveMtQualification ? 'text-blue-800' : 'text-stone-400'}`}
                      >
                        Generer NDT-rapport (MPI)
                        {!effectiveMtQualification && (
                          <span className="block text-xs text-stone-400 font-normal">
                            {currentUser?.isAdmin ? 'Velg inspektør med MT-sertifisering' : 'Krever MT-sertifisering'}
                          </span>
                        )}
                      </label>
                    </div>
                  )}
                  
                  {/* Inspection Status - shown for BOTH existing and new equipment */}
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 mb-1">Status *</label>
                    <div className="grid grid-cols-5 gap-2">
                      {INSPECTION_STATUSES.map(status => (
                        <button
                          key={status.value}
                          type="button"
                          onClick={() => updateEntry(entry.id, { 
                            inspectionStatus: status.value,
                            remarks: status.value === 'OK' ? '' : entry.remarks 
                          })}
                          className={`px-3 py-3 text-sm font-bold rounded-xl transition-all ${status.color} text-white ${
                            entry.inspectionStatus === status.value
                              ? status.value === 'OK' 
                                ? '' // OK stays plain green, no ring
                                : `ring-4 ${status.selectedRing} scale-105 shadow-lg`
                              : 'opacity-40 hover:opacity-70'
                          }`}
                        >
                          {status.value}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Remarks - only shown when status is not OK */}
                  {entry.inspectionStatus && entry.inspectionStatus !== 'OK' && (
                    <div>
                      <label className="block text-xs font-semibold text-stone-500 mb-1">
                        Merknader *
                      </label>
                      <textarea
                        value={entry.remarks || ''}
                        onChange={(e) => updateEntry(entry.id, { remarks: e.target.value })}
                        placeholder="Beskriv avvik eller anmerkninger..."
                        rows={3}
                        className={`w-full px-4 py-3 ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none resize-none`}
                      />
                    </div>
                  )}
                  
                  {/* Photo upload - only shown when status is not OK */}
                  {entry.inspectionStatus && entry.inspectionStatus !== 'OK' && (
                    <div>
                      <label className="block text-xs font-semibold text-stone-500 mb-1">
                        Bilde av avvik
                      </label>
                      {entry.inspectionPhoto ? (
                        <div className="relative">
                          <img 
                            src={entry.inspectionPhoto} 
                            alt="Avviksbilde" 
                            className="w-full max-h-48 object-contain rounded-xl border-2 border-stone-200"
                          />
                          <button
                            type="button"
                            onClick={() => updateEntry(entry.id, { inspectionPhoto: undefined })}
                            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <label className={`flex items-center justify-center gap-3 ${TOUCH.inputHeight} px-4 py-3 bg-stone-50 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-[#1E3A5F] hover:bg-stone-100 transition-colors`}>
                          <Camera size={24} className="text-stone-400" />
                          <span className="text-stone-500">Ta bilde eller velg fil</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                const reader = new FileReader()
                                reader.onloadend = () => {
                                  updateEntry(entry.id, { inspectionPhoto: reader.result as string })
                                }
                                reader.readAsDataURL(file)
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                  
                </div>
              )}
            </div>
          )
        })}
        
        <div className="h-28" />
      </div>

      {/* Fixed bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t-2 border-stone-200 shadow-lg">
        <div className="p-3 space-y-2">
          {/* Role-based selection */}
          {currentUser?.isAdmin ? (
            // Admin selects inspector (who performed)
            inspectors.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-stone-600 whitespace-nowrap">
                  Utført av:
                </label>
                <select
                  value={selectedInspectorId}
                  onChange={(e) => setSelectedInspectorId(e.target.value)}
                  className={`flex-1 px-4 py-3 ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:border-[#1E3A5F] focus:outline-none bg-white`}
                >
                  <option value="">Velg inspektør...</option>
                  {inspectors.map(inspector => (
                    <option key={inspector.id} value={inspector.id}>
                      {inspector.name}
                    </option>
                  ))}
                </select>
              </div>
            )
          ) : null}
          
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || result?.success || validEntriesCount === 0 || (currentUser?.isAdmin && !selectedInspectorId)}
              className={`flex-1 flex items-center justify-center gap-3 ${TOUCH.buttonHeight} px-6 py-4 bg-[#1E3A5F] text-white text-lg font-semibold rounded-2xl hover:bg-[#2a4a73] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={24} className="animate-spin" />
                  <span>Oppretter...</span>
                </>
              ) : result?.success ? (
                <>
                  <CheckCircle size={24} />
                  <span>Fullført!</span>
                </>
              ) : (
                <span>
                  {entries.some(e => e.generateNdtReport) 
                    ? 'Opprett kontrollkort & MT-rapport' 
                    : 'Opprett kontrollkort'}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SamlelistePage
